import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeIcon from '@mui/icons-material/Badge';
import { useAuth } from '../../auth/AuthContext';
import { obtenerTurno, listarDiasNoLaborables, obtenerConfigTurnero, listarRango, crearTurno, eliminarTurno, cambiarEstado, revertirTurno, editarDatosTurno, obtenerHorariosDisponibles, agregarObservacionTurno, obtenerOcupacionBloque, obtenerTurnosChatbot, buscarTurnosPorPersona } from '../../services/turneroService';
import { esSinPresentacion, metaTipoTurnoAnses } from '../../utils/tiposTurnoAnses';
import { buscarClientes } from '../../services/clienteService';
import { usuarioService } from '../../services/usuarioService';
import DatosMaestrosService from '../../services/datosMaestrosService';
import ComboboxDatosMaestros from '../../components/ComboboxDatosMaestros';
import SelectorAreaAtencion from '../../components/recepcion/SelectorAreaAtencion';
import VincularClienteTurno from '../../components/recepcion/VincularClienteTurno';
import ModalReingresoCliente from '../../components/recepcion/ModalReingresoCliente';
import BadgeReingreso from '../../components/recepcion/BadgeReingreso';
import ModalBuscarAtendidoHoy from '../../components/recepcion/ModalBuscarAtendidoHoy';
import DetalleTramiteExpres, { AvisoTramiteExpres } from '../../components/recepcion/DetalleTramiteExpres';
import RielTurno from '../../components/recepcion/RielTurno';
import { ultimaAtencionFinalizada } from '../../utils/ultimaAtencionCliente';
import ChipMotivoExpres from '../../components/recepcion/ChipMotivoExpres';
import { etiquetaMotivoExpres, etiquetaCortaMotivoExpres, ESTILO_EXPRES } from '../../constants/tramiteExpres';
import { derivarAtencion, etiquetaArea } from '../../constants/areasAtencion';
import ModalDetalleTurnoAnses from '../../components/ModalDetalleTurnoAnses';
import { colorTramite, bgTramite } from '../../constants/tiposTramiteTurno';
import { listarTiposTurno } from '../../services/tipoTurnoService';
import { ESTADOS_TURNERO, ESTILO_SOBRETURNO, estiloEstado } from '../../constants/estadosTurno';
import { etiquetaMotivoNoAvance } from '../../constants/resultadosAtencion';
import '../../style/pages/HomePage.css';
import styles from '../../style/pages/Turnero.module.css';
import PageHeader from '../../components/ui/PageHeader';
import { apellidoNombre, textoPersona } from '../../utils/nombrePersona';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';
import { coincideBusqueda } from '../../utils/textoComparable';

// La semana arranca en domingo, igual que Date.getDay() (0=dom … 6=sáb), así que las
// columnas del calendario se indexan directo con getDay() sin corrimiento.
const COLS_CAL = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// Respaldo mínimo por si el catálogo no responde. La fuente de verdad es la tabla tipo_turno
// (Datos Maestros): esta lista ofrecía tipos que el backend no aceptaba —RECONOCIMIENTO,
// AMPARO_PNC— y esos turnos terminaban guardados como "consulta general".
const TIPOS_TRAMITE_RESPALDO = [
    { valor: 'RTI',        etiqueta: 'RTI – Retiro Transitorio por Invalidez' },
    { valor: 'ART',        etiqueta: 'ART – Accidente de Trabajo' },
    { valor: 'JUBILACION', etiqueta: 'Jubilación' },
    { valor: 'PUAM',       etiqueta: 'PUAM' },
    { valor: 'PLAN_PAGO',  etiqueta: 'Plan de Pago 27.705' },
    { valor: 'PENSION',    etiqueta: 'Pensión' },
    { valor: 'REAJUSTE',   etiqueta: 'Reajuste de Haberes' },
    { valor: 'GANANCIAS',  etiqueta: 'Impuesto a las Ganancias' },
    { valor: 'MINUSVALIA', etiqueta: 'Minusvalía' },
    { valor: 'DECLARATORIA_HEREDEROS', etiqueta: 'Declaratoria de Herederos' },
    { valor: 'RECONOCIMIENTO', etiqueta: 'Reconocimiento de Servicios' },
    { valor: 'PNC',        etiqueta: 'PNC' },
    { valor: 'AMPARO_PNC', etiqueta: 'Amparo PNC' },
    { valor: 'AMPARO',     etiqueta: 'Amparo por mora (ANSES)' },
    { valor: 'ANSES',      etiqueta: 'Trámite ANSES General' },
    { valor: 'GENERAL',    etiqueta: 'Consulta General' },
];

// Estados desde los que ya no se ofrece ninguna acción de cambio. FINALIZADO entra acá
// porque el backend lo trata como final (TurnoRecepcionService.ESTADOS_FINALES) y rechaza
// cualquier transición: sin él, el modal ofrecía botones que siempre fallaban.
const ESTADOS_FINALES = new Set(['ASISTIO', 'FINALIZADO', 'NO_ASISTIO', 'CANCELADO']);

// Acciones manuales de cambio de estado que se ofrecen desde el modal de detalle. CREADO
// no está: es el estado con el que nace el turno y no tiene sentido volver a él a mano.
// PENDIENTE y CONFIRMADO sí, porque son el ida y vuelta del seguimiento telefónico:
// recepción llama, no atienden y lo deja PENDIENTE; vuelve a llamar y lo pasa a CONFIRMADO.
const ACCIONES_MODAL = new Set(['PENDIENTE', 'CONFIRMADO', 'REPROGRAMADO', 'ASISTIO', 'NO_ASISTIO', 'CANCELADO']);

const TIPOS_TURNO = [
    { valor: 'PRESENCIAL',   etiqueta: 'Presencial',   icono: 'bi-building' },
    { valor: 'VIDEOLLAMADA', etiqueta: 'Videollamada', icono: 'bi-camera-video-fill' },
];

// Valores del <select> de referencia que no son un id del catálogo. No pueden ser '' porque
// ése ya significa "todas": por eso dos centinelas.
//
// Con estos dos, las opciones del filtro cubren el 100% de los turnos y no hay ninguno que sea
// imposible de encontrar: o apunta al catálogo, o tiene una referencia escrita a mano que no
// está en él ("su cuñada marisa zanotti"), o no tiene nada.
const SIN_REFERENCIA = 'SIN_REFERENCIA';
const REFERENCIA_LIBRE = 'REFERENCIA_LIBRE';

const normalizarRef = (v) => String(v || '').trim().toUpperCase();

/**
 * Si el turno entra en la búsqueda por texto. Mismos campos en las dos vistas (calendario y
 * agenda): quien busca "Pérez" espera encontrarlo mire donde mire.
 */
function coincideTextoTurno(turno, consulta) {
    // Sin tildes y por palabras: "perez" encuentra "PÉREZ" (ver coincideBusqueda).
    return coincideBusqueda(
        [turno.apellido, turno.nombre, turno.cuil, turno.dni, turno.telefonoCliente, turno.motivo], consulta);
}

/**
 * Si el turno corresponde a la referencia elegida.
 *
 * Se resuelve por la FK al catálogo, que es el dato bueno. Los turnos viejos solo tienen el
 * texto —la FK llegó después, ver V212/V214— y para ésos se compara el nombre normalizado: si
 * el texto dice exactamente el nombre de la referencia, es esa referencia. No se hace nada más
 * astuto a propósito: "OTRO CLIENTE (TELLO CARMEN, REF: CARTA)" no es la referencia CARTA, es
 * la historia de cómo llegó esa persona.
 */
function coincideReferenciaTurno(turno, idReferencia, referenciasPorId) {
    if (!idReferencia) return true;
    if (idReferencia === SIN_REFERENCIA) {
        return !turno.idReferencia && !normalizarRef(turno.referencia);
    }
    if (idReferencia === REFERENCIA_LIBRE) {
        return !turno.idReferencia && Boolean(normalizarRef(turno.referencia));
    }
    if (String(turno.idReferencia ?? '') === String(idReferencia)) return true;
    if (turno.idReferencia) return false;
    const ref = referenciasPorId.get(String(idReferencia));
    return Boolean(ref) && normalizarRef(turno.referencia) === normalizarRef(ref.nombre);
}

/** <select> de referencia, igual en las dos vistas. */
function SelectorFiltroReferencia({ valor, onChange, referencias }) {
    return (
        <div className={styles.filtroCampo}>
            <label className={styles.filtroLabel}>
                <i className="bi bi-megaphone me-2"></i>Referencia
            </label>
            <select
                className={`form-select ${styles.filtroInput}`}
                value={valor}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="">Todas las referencias</option>
                <option value={SIN_REFERENCIA}>— Sin referencia —</option>
                <option value={REFERENCIA_LIBRE}>— Anotada a mano (fuera del catálogo) —</option>
                {referencias.map((r) => (
                    <option key={r.id} value={r.id}>{r.nombre}</option>
                ))}
            </select>
        </div>
    );
}

// Filtro de estado de la vista Agenda: incluye "Todos" y los mismos estados que ya ofrecía
// el <select> (a diferencia del panel del día en CalendarioView, que no se tocó).
const FILTROS_ESTADO_AGENDA = [
    { valor: '', etiqueta: 'Todos los estados' },
    { valor: 'CREADO', etiqueta: 'Creado' },
    { valor: 'CONFIRMADO', etiqueta: 'Confirmado' },
    { valor: 'REPROGRAMADO', etiqueta: 'Reprogramado' },
    { valor: 'ASISTIO', etiqueta: 'Asistió' },
    { valor: 'EN_CURSO', etiqueta: 'En curso' },
    { valor: 'EN_ANSES', etiqueta: 'En ANSES' },
    { valor: 'NO_ASISTIO', etiqueta: 'No asistió' },
    { valor: 'FINALIZADO', etiqueta: 'Finalizado' },
    { valor: 'CANCELADO', etiqueta: 'Cancelado' },
];

const FORM_VACIO = { nombre: '', apellido: '', cuil: '', telefono: '', tipoTramite: 'RTI', referencia: '', idReferencia: null, tipoTurno: 'PRESENCIAL', idUsuarioAtiende: '', atencion: '', tieneCarpeta: false, esPrimeraVez: '', motivo: '' };

// onChange del ComboboxDatosMaestros solo devuelve el nombre; hay que resolver el id
// contra la lista cargada (mismo patrón que SelectorReferenciaCliente.js para cliente).
function resolverCambioReferencia(nuevoNombre, referenciasDisponibles) {
    const encontrada = referenciasDisponibles.find(
        (r) => r.nombre?.toLowerCase() === nuevoNombre?.toLowerCase()
    );
    return { referencia: nuevoNombre, idReferencia: encontrada ? encontrada.id : null };
}

// El catálogo se guarda en un módulo aparte del estado de React para que etiquetaTipo() —que se
// llama desde el render de cada tarjeta— no necesite recibirlo por props en toda la pantalla.
let CATALOGO_TIPOS = [];

function etiquetaTipo(valor) {
    const delCatalogo = CATALOGO_TIPOS.find(x => x.codigo === valor);
    if (delCatalogo) return delCatalogo.etiqueta;
    const t = TIPOS_TRAMITE_RESPALDO.find(x => x.valor === valor);
    return t ? t.etiqueta.split(' – ')[0] : valor;
}

function etiquetaTipoTurno(valor) {
    const t = TIPOS_TURNO.find(x => x.valor === valor);
    return t ? t.etiqueta : valor;
}

// "2026-07-23" → "23 jul 2026"
function formatearFechaLarga(iso) {
    if (!iso) return '';
    const texto = isoAFecha(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
    return texto.replace('.', '');
}

// Fecha compacta para la tabla del chatbot. Con siete columnas, dos fechas escritas largo
// ("02 de sept de 2026") se llevaban casi la mitad del ancho y empujaban el botón de detalle
// fuera de la vista.
function formatearFechaCorta(iso) {
    if (!iso) return '';
    const d = isoAFecha(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatearFechaHoraCorta(iso) {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    const fecha = d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    return `${fecha} ${d.toTimeString().substring(0, 5)}`;
}

function formatearCanal(canal) {
    if (!canal) return '';
    return canal.charAt(0).toUpperCase() + canal.slice(1).toLowerCase();
}

// Los timestamps de la atención (inicioAtencion/finAtencion) son LocalDateTime, no LocalDate:
// van por Date y no por isoAFecha, que solo parsea la parte de la fecha.
function formatearHoraDe(valor) {
    if (!valor) return '';
    const fecha = new Date(valor);
    return Number.isNaN(fecha.getTime()) ? '' : fecha.toTimeString().substring(0, 5);
}

function formatearDuracionAtencion(inicio, fin) {
    if (!inicio || !fin) return '';
    const inicioMs = new Date(inicio).getTime();
    const finMs = new Date(fin).getTime();
    if (Number.isNaN(inicioMs) || Number.isNaN(finMs) || finMs < inicioMs) return '';
    const totalMinutos = Math.round((finMs - inicioMs) / 60000);
    const horas = Math.floor(totalMinutos / 60);
    const minutos = totalMinutos % 60;
    return horas > 0 ? `${horas} h ${minutos} min` : `${minutos} min`;
}

// Por qué el turno ya no acepta acciones desde el turnero. No es lo mismo en los cuatro
// estados: ASISTIO no está cerrado —el cliente está esperando y el flujo sigue en Atención—
// mientras que cancelado, no asistió y finalizado sí terminaron.
function leyendaEstadoFinal(estado, etiqueta) {
    if (estado === 'ASISTIO') {
        return {
            titulo: 'El cliente está en la cola de atención',
            ayuda: 'La atención se sigue desde la pantalla de Atención, no desde el turnero.',
        };
    }
    if (estado === 'FINALIZADO') {
        return {
            titulo: 'Turno finalizado',
            ayuda: 'La atención ya se cerró. Si fue un error, se puede volver atrás.',
        };
    }
    return {
        titulo: `Turno cerrado como «${etiqueta}»`,
        ayuda: 'Ya no admite cambios de estado. Si fue un error, se puede volver atrás.',
    };
}

// "2026-07-23T14:30:00" → "23 jul 2026, 14:30" — mismo criterio que formatearFechaLarga
// (fecha corta en es-AR) más la hora, para creadoEn (LocalDateTime), que a diferencia de
// fechaTurno sí necesita distinguirse por hora: dos turnos pueden agendarse el mismo día
// pero haberse cargado en momentos bien distintos (uno manual a la mañana, otro por el
// chatbot a la noche).
function formatearFechaHoraCreacion(iso) {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    const fecha = d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', '');
    const hora = d.toTimeString().substring(0, 5);
    return `${fecha}, ${hora}`;
}

function fechaToISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

// El bloque configurado más cercano a una hora "HH:mm" sugerida (puede no caer justo en un
// bloque, ej. la hora de un evento ya cargado). Empate: gana el más temprano, mismo criterio que
// CalculadoraCitacionService en el backend.
function bloqueMasCercano(bloques, horaObjetivo) {
    const aMinutos = (hhmm) => {
        const [h, m] = hhmm.split(':').map(Number);
        return h * 60 + m;
    };
    const objetivo = aMinutos(horaObjetivo);
    return bloques.reduce((mejor, actual) => {
        const distancia = Math.abs(aMinutos(actual) - objetivo);
        const distanciaMejor = Math.abs(aMinutos(mejor) - objetivo);
        return distancia < distanciaMejor ? actual : mejor;
    }, bloques[0]);
}

function sumarDias(fecha, n) {
    const d = new Date(fecha);
    d.setDate(d.getDate() + n);
    return d;
}

// Convierte un ISO date string "YYYY-MM-DD" a una Date local (sin desfase UTC)
function isoAFecha(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
}

// Índice de Date.getDay() (0=domingo) → diaSemana del backend (java.time.DayOfWeek.getValue():
// 1=lunes..7=domingo). Cada día de la semana tiene su propio horario/almuerzo — ver
// TurneroHorarioDiaEntity — a diferencia de antes, donde un solo horario valía para todos
// los días habilitados.
const DIA_SEMANA_POR_GETDAY = [7, 1, 2, 3, 4, 5, 6];

function horarioDelDia(config, fecha) {
    const diaSemana = DIA_SEMANA_POR_GETDAY[fecha.getDay()];
    return config?.horariosPorDia?.find(d => d.diaSemana === diaSemana) || null;
}

// Bloques horarios de la jornada para una fecha puntual, según el horario propio de ese día
// de la semana: de horaInicio a horaFin inclusive, paso duracionMinutos (global). Mismo
// algoritmo que el generarBloquesHorarios() del backend (TurnoRecepcionService), para que la
// grilla del front coincida con la que valida el servidor. Devuelve ["08:00", "08:30", …].
function generarBloquesHorarios(config, fecha) {
    const dia = horarioDelDia(config, fecha);
    if (!dia?.activo || !dia.horaInicio || !dia.horaFin || !(config?.duracionMinutos > 0)) return [];
    const [hI, mI] = dia.horaInicio.split(':').map(Number);
    const [hF, mF] = dia.horaFin.split(':').map(Number);
    let mins = hI * 60 + mI;
    const finMins = hF * 60 + mF;
    if (finMins < mins) return [];
    // Franja de almuerzo opcional — [inicio, fin), mismo criterio que dentroDelAlmuerzo() del
    // backend (TurnoRecepcionService). Sin configurar (los dos campos vacíos), no corta nada:
    // mismo comportamiento que antes de este campo.
    let almuerzoInicioMins = null;
    let almuerzoFinMins = null;
    if (dia.horarioAlmuerzoInicio && dia.horarioAlmuerzoFin) {
        const [hAI, mAI] = dia.horarioAlmuerzoInicio.split(':').map(Number);
        const [hAF, mAF] = dia.horarioAlmuerzoFin.split(':').map(Number);
        almuerzoInicioMins = hAI * 60 + mAI;
        almuerzoFinMins = hAF * 60 + mAF;
    }
    const bloques = [];
    while (mins <= finMins) {
        const enAlmuerzo = almuerzoInicioMins !== null && mins >= almuerzoInicioMins && mins < almuerzoFinMins;
        if (!enAlmuerzo) {
            bloques.push(`${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`);
        }
        mins += config.duracionMinutos;
    }
    return bloques;
}

// Domingo de la semana a la que pertenece la fecha dada. La semana arranca domingo, igual
// que la grilla del calendario mensual, para que la fila que se ve ahí sea exactamente la
// semana que muestra la agenda.
function domingoDeLaSemana(fecha) {
    const d = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
    d.setDate(d.getDate() - d.getDay()); // getDay(): 0=dom
    return d;
}

// Nombre de columna de la agenda semanal, en mayúsculas: "LUNES", "MARTES", …
const NOMBRES_DIA = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];

// ── BadgeEstado ─────────────────────────────────────────────────────────────

function BadgeEstado({ estado, tamaño = 'normal' }) {
    const e = estiloEstado(estado);
    return (
        <span
            className="rec-badge-estado"
            style={{
                background: e.bg, border: `1px solid ${e.border}`, color: e.text,
                ...(tamaño === 'normal' ? { padding: '0.4rem 0.85rem', fontSize: '0.85rem' } : {}),
            }}
        >
            {e.etiqueta}
        </span>
    );
}

function BadgeSobreturno({ tamaño = 'normal' }) {
    return (
        <span
            className="rec-badge-sobreturno"
            style={tamaño === 'normal' ? { padding: '0.4rem 0.85rem', fontSize: '0.78rem' } : undefined}
        >
            <i className="bi bi-exclamation-diamond-fill me-1" />
            Sobreturno
        </span>
    );
}

// ── CalloutEstado ────────────────────────────────────────────────────────────

/**
 * Aviso destacado para los tres desenlaces que antes se leían como un renglón más de la
 * lista: reprogramado, cancelado y no asistió. Son la información más importante del turno
 * cuando pasaron, así que van arriba de todo y con el color del estado, no perdidos entre
 * el teléfono y el canal.
 *
 * Devuelve null cuando no hay nada que destacar (el turno sigue su curso normal).
 */
// Quién pidió la baja. Importa distinguirlo: que el cliente cancele solo por el bot no es lo
// mismo que una baja decidida en el mostrador, y hasta ahora no quedaba registro de ninguna de
// las dos (el turno cancelado se borraba a las 24 horas). Los turnos cancelados antes de que se
// guardara el dato no dicen quién fue, en vez de atribuírselo a alguien.
function tituloCancelacion(turno) {
    if (turno.origenCancelacion === 'CHATBOT_EXTERNO') return 'Turno cancelado por el cliente (BOT)';
    if (turno.origenCancelacion === 'MANUAL') {
        return turno.canceladoPorNombre
            ? `Turno cancelado por ${turno.canceladoPorNombre}`
            : 'Turno cancelado desde el estudio';
    }
    return 'Turno cancelado';
}

/**
 * Qué cambió en un turno mientras estaba abierto en pantalla (20-oct-2026: el cliente lo canceló por
 * el bot y en el mostrador seguía viéndose "Confirmado"; al querer cancelarlo salía "estado final").
 * Null si no cambió nada relevante.
 */
export function avisoTurnoCambiado(antes, ahora) {
    if (!antes || !ahora || antes.estado === ahora.estado) return null;
    if (ahora.estado === 'CANCELADO') {
        const cuando = ahora.fechaCancelacion ? ` el ${formatearFechaHoraCreacion(ahora.fechaCancelacion)}` : '';
        return `${tituloCancelacion(ahora)}${cuando}. Se actualizó el turno.`;
    }
    return `Mientras estaba abierto, el turno pasó a "${estiloEstado(ahora.estado).etiqueta}". Se actualizó el turno.`;
}

function CalloutEstado({ turnoSel }) {
    const esReprogramado = Boolean(turnoSel.fechaReprogramada);
    const esCancelado = turnoSel.estado === 'CANCELADO';
    const esNoAsistio = turnoSel.estado === 'NO_ASISTIO';

    if (!esReprogramado && !esCancelado && !esNoAsistio) return null;

    // Cancelado y no asistió mandan sobre reprogramado: si un turno se reprogramó y después
    // se canceló, lo que importa es que está cancelado.
    if (esCancelado || esNoAsistio) {
        const e = estiloEstado(turnoSel.estado);
        return (
            <div
                className={`${styles.callout} ${styles.calloutNegativo}`}
                style={{ background: e.bg, borderColor: e.border }}
            >
                <div className={styles.calloutIcono} style={{ background: e.border }}>
                    <i className={`bi ${esCancelado ? 'bi-slash-circle-fill' : 'bi-x-circle-fill'}`} />
                </div>
                <div className={styles.calloutTexto}>
                    <div className={styles.calloutTitulo} style={{ color: e.text }}>
                        {esCancelado ? tituloCancelacion(turnoSel) : 'El cliente no asistió'}
                    </div>
                    {turnoSel.motivoEstado ? (
                        <div className={styles.calloutDetalle}>{turnoSel.motivoEstado}</div>
                    ) : (
                        <div className={styles.calloutDetalleVacio}>Sin motivo registrado</div>
                    )}
                    {esCancelado && turnoSel.fechaCancelacion && (
                        <div className={styles.calloutDetalleVacio}>
                            {formatearFechaHoraCreacion(turnoSel.fechaCancelacion)}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Al reprogramar, el backend pisa fechaTurno con la nueva y guarda lo mismo en
    // fechaReprogramada, así que no hay fecha original para mostrar: el aviso sirve para
    // dejar claro que la fecha de arriba no es la que se había pactado al principio.
    return (
        <div className={`${styles.callout} ${styles.calloutReprogramado}`}>
            <div className={styles.calloutIcono} style={{ background: '#f59e0b' }}>
                <i className="bi bi-arrow-repeat" />
            </div>
            <div className={styles.calloutTexto}>
                <div className={styles.calloutTitulo} style={{ color: '#92400e' }}>
                    Turno reprogramado
                </div>
                <div className={styles.calloutDetalle}>
                    Movido a{' '}
                    <strong>{formatearFechaLarga(turnoSel.fechaReprogramada)}</strong>
                    {turnoSel.horarioReprogramado && <> a las <strong>{turnoSel.horarioReprogramado}</strong></>}
                </div>
            </div>
        </div>
    );
}

// ── PanelAtencion ────────────────────────────────────────────────────────────

/** Si el turno tiene algo de la atención para mostrar (el modal decide con esto sus columnas). */
function tieneDatosAtencion(turnoSel) {
    return Boolean(turnoSel.inicioAtencion || turnoSel.notasAtencion
        || turnoSel.operadorNombre || etiquetaMotivoNoAvance(turnoSel.motivoNoAvance));
}

/**
 * Qué pasó cuando el cliente vino. Estos datos los carga VistaDetalladaAtencion al atender
 * el turno y hasta ahora no se veían desde el turnero: había que entrar a la atención para
 * saber quién lo atendió o qué se resolvió.
 *
 * Devuelve null si el turno todavía no fue atendido.
 */
function PanelAtencion({ turnoSel }) {
    const duracion = formatearDuracionAtencion(turnoSel.inicioAtencion, turnoSel.finAtencion);
    const horaInicio = formatearHoraDe(turnoSel.inicioAtencion);
    const horaFin = formatearHoraDe(turnoSel.finAtencion);
    const motivoNoAvance = etiquetaMotivoNoAvance(turnoSel.motivoNoAvance);

    if (!tieneDatosAtencion(turnoSel)) return null;

    return (
        <section className={`${styles.detalleTarjeta} ${styles.detalleTarjetaAtencion}`}>
            <div className={styles.detalleTarjetaTitulo}>
                <i className="bi bi-clipboard2-check" />
                Atención
            </div>

            <div className={styles.detalleKV}>
                {turnoSel.operadorNombre && <DatoTurno etiqueta="Atendió" valor={turnoSel.operadorNombre} />}
                {horaInicio && <DatoTurno etiqueta="Horario" valor={`${horaInicio}${horaFin ? ` – ${horaFin}` : ''}`} />}
                {duracion && <DatoTurno etiqueta="Duración" valor={duracion} />}
            </div>

            {turnoSel.notasAtencion && (
                <div className={styles.detalleMotivo}>
                    <span className={styles.detalleKVEtiqueta}>Notas / resultado</span>
                    <p>{turnoSel.notasAtencion}</p>
                </div>
            )}

            {motivoNoAvance && (
                <div className={styles.detalleAlerta} role="note">
                    <i className="bi bi-exclamation-triangle-fill" />
                    <div>
                        <div className={styles.detalleAlertaTitulo}>El trámite no avanzó · {motivoNoAvance}</div>
                        {turnoSel.observacionMotivoNoAvance && (
                            <div className={styles.detalleAlertaTexto}>{turnoSel.observacionMotivoNoAvance}</div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}

/** Un dato del turno como clave-valor: etiqueta chica arriba, valor abajo. */
function DatoTurno({ etiqueta, valor, apagado = false }) {
    return (
        <div className={styles.detalleKVItem}>
            <span className={styles.detalleKVEtiqueta}>{etiqueta}</span>
            <span className={`${styles.detalleKVValor} ${apagado ? styles.detalleKVValorApagado : ''}`}>{valor || '-'}</span>
        </div>
    );
}

// Colores de los chips del header claro. Estado, sobreturno y exprés salen de sus constantes;
// reingreso y ANSES espejan .rec-badge-reingreso y el chip ANSES del panel diario.
const ESTILO_CHIP_REINGRESO = { bg: '#fff7ed', border: '#fdba74', text: '#c2410c' };
const ESTILO_CHIP_ANSES = { bg: '#e0f2fe', border: '#7dd3fc', text: '#0369a1' };

function estiloChip({ bg, border, text }) {
    return { background: bg, border: `1px solid ${border}`, color: text };
}

// ── Bitácora de observaciones ────────────────────────────────────────────────

// Cada línea la arma el backend como "[dd/MM/yyyy HH:mm · Autor] texto" (anotarEnBitacora,
// separadas por "\n"). Esto solo la lee para dibujarla: una línea con otro formato se muestra tal
// cual, sin autor ni fecha.
const LINEA_BITACORA = /^\[(.+?) · (.+?)\] ([\s\S]*)$/;

function parsearBitacora(observaciones) {
    if (!observaciones) return [];
    return String(observaciones)
        .split('\n')
        .map((linea) => linea.trim())
        .filter(Boolean)
        .map((linea) => {
            const m = linea.match(LINEA_BITACORA);
            return m ? { fecha: m[1], autor: m[2], texto: m[3] } : { fecha: null, autor: null, texto: linea };
        });
}

function BitacoraTimeline({ observaciones }) {
    const entradas = parsearBitacora(observaciones);
    if (entradas.length === 0) {
        return <p className={styles.detalleTimelineVacio}>Todavía no hay observaciones.</p>;
    }
    return (
        <ol className={styles.detalleTimeline}>
            {entradas.map((entrada, i) => (
                <li key={i} className={styles.detalleTimelineItem}>
                    <span className={styles.detalleTimelineAvatar} aria-hidden="true">
                        {entrada.autor ? entrada.autor.trim().charAt(0).toUpperCase() : <i className="bi bi-chat-left-text" />}
                    </span>
                    <div className={styles.detalleTimelineCuerpo}>
                        {(entrada.autor || entrada.fecha) && (
                            <div className={styles.detalleTimelineCabecera}>
                                <strong>{entrada.autor}</strong>
                                <span>{entrada.fecha}</span>
                            </div>
                        )}
                        <div className={styles.detalleTimelineTexto}>{entrada.texto}</div>
                    </div>
                </li>
            ))}
        </ol>
    );
}

// ── SubPanelMotivo ───────────────────────────────────────────────────────────

function SubPanelMotivo({ estadoPendiente, motivoInput, onChange, cambiandoEstado, onVolver, onConfirmar, setError }) {
    const e = estiloEstado(estadoPendiente);
    return (
        <div className={styles.subPanelMotivo}>
            <div className={styles.subPanelTituloMotivo}>
                <span style={{ background: e.bg, border: `1px solid ${e.border}`, color: e.text, borderRadius: 8, padding: '2px 10px', marginRight: 6 }}>
                    {e.etiqueta}
                </span>
                — Ingresá el motivo
            </div>
            <textarea
                className="form-control mb-2"
                rows={2}
                placeholder="Motivo (obligatorio)"
                value={motivoInput}
                onChange={ev => onChange(ev.target.value)}
                style={{ fontSize: '0.85rem' }}
                autoFocus
            />
            <div className="d-flex gap-2">
                <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={onVolver} disabled={cambiandoEstado}>
                    Volver
                </button>
                <button
                    className="btn btn-sm btn-primary flex-fill"
                    onClick={() => {
                        if (!motivoInput.trim()) { setError('El motivo es obligatorio.'); return; }
                        setError('');
                        onConfirmar(estadoPendiente, { motivo: motivoInput.trim() });
                    }}
                    disabled={cambiandoEstado || !motivoInput.trim()}
                >
                    {cambiandoEstado && <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 1 }} />}
                    Confirmar
                </button>
            </div>
        </div>
    );
}

// ── SubPanelReprogramar ──────────────────────────────────────────────────────

function SubPanelReprogramar({ reprogramarFecha, setReprogramarFecha, reprogramarHorario, setReprogramarHorario, horasDisponibles, cargandoHoras, cambiandoEstado, onVolver, onConfirmar, setError }) {
    return (
        <div className={styles.subPanelReprogramar}>
            <div className={styles.subPanelTituloReprogramar}>
                <i className="bi bi-calendar-event me-1" /> Reprogramar turno
            </div>
            <div className="mb-2">
                <label className={`form-label ${styles.subPanelLabel}`}>Nueva fecha *</label>
                <input
                    type="date"
                    className="form-control form-control-sm"
                    min={new Date().toISOString().split('T')[0]}
                    value={reprogramarFecha}
                    onChange={e => { setReprogramarFecha(e.target.value); setReprogramarHorario(''); }}
                />
            </div>
            <div className="mb-3">
                <label className={`form-label ${styles.subPanelLabel}`}>Nuevo horario *</label>
                {cargandoHoras ? (
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 1 }} /> Cargando horarios…
                    </div>
                ) : (
                    <select
                        className="form-select form-select-sm"
                        value={reprogramarHorario}
                        onChange={e => setReprogramarHorario(e.target.value)}
                        disabled={!reprogramarFecha || horasDisponibles.length === 0}
                    >
                        <option value="">
                            {!reprogramarFecha ? 'Primero elegí la fecha' : horasDisponibles.length === 0 ? 'Sin horarios disponibles' : 'Seleccionar horario…'}
                        </option>
                        {horasDisponibles.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                )}
            </div>
            <div className="d-flex gap-2">
                <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={onVolver} disabled={cambiandoEstado}>
                    Volver
                </button>
                <button
                    className="btn btn-sm btn-primary flex-fill"
                    onClick={() => {
                        if (!reprogramarFecha || !reprogramarHorario) { setError('Fecha y horario son obligatorios.'); return; }
                        setError('');
                        onConfirmar('REPROGRAMADO', { nuevaFecha: reprogramarFecha, nuevoHorario: reprogramarHorario });
                    }}
                    disabled={cambiandoEstado || !reprogramarFecha || !reprogramarHorario}
                >
                    {cambiandoEstado && <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 1 }} />}
                    Confirmar
                </button>
            </div>
        </div>
    );
}

// ── ModalDetalleTurno ────────────────────────────────────────────────────────

/**
 * Catálogo de tipos de turno. Es un hook y no una constante porque la lista vive en la base
 * (Datos Maestros) y la manejan en el estudio. Lo usan los dos formularios del archivo —el de
 * alta y el de edición—, que están en componentes distintos.
 *
 * Si el catálogo no responde se cae al respaldo local en vez de dejar el selector vacío: es
 * preferible ofrecer algo desactualizado a bloquear la carga de un turno.
 */
/**
 * Los tipos que se pueden elegir al cargar un turno a mano.
 *
 * Se filtran los que el catálogo marca como no seleccionables. Hoy el único es "Turno ANSES":
 * recepción lo estaba usando para anotar una vista, y eso crea un turno que PARECE de ANSES sin
 * tener uno detrás — sin citación vinculada, sin presentación y sin aparecer en el listado del
 * módulo. Una vista se carga desde el trámite, eligiendo el tipo de turno ANSES.
 *
 * No alcanzaba con mirar esSistema: "Consulta general" también lo es y sí se elige a mano.
 *
 * El catálogo completo se sigue guardando para poder ETIQUETAR los turnos que ya tienen esos
 * tipos; lo que se restringe es la elección, no la lectura.
 */
function useTiposTurno() {
    const [tipos, setTipos] = useState([]);

    useEffect(() => {
        let cancelado = false;
        listarTiposTurno(true)
            .then(lista => {
                if (cancelado) return;
                setTipos((lista || []).filter(t => t.seleccionable !== false));
                CATALOGO_TIPOS = lista; // etiquetaTipo() se llama fuera de los componentes
            })
            .catch(() => { if (!cancelado) setTipos([]); });
        return () => { cancelado = true; };
    }, []);

    // Alfabético por etiqueta, no por el "orden" del catálogo: en el turnero la lista se usa
    // para buscar un tipo puntual mientras hay alguien esperando en el mostrador, y con 21
    // opciones se encuentra más rápido leyendo alfabéticamente. El campo "orden" de Datos
    // Maestros sigue mandando en las demás pantallas. localeCompare('es') para que los
    // acentos y la ñ caigan donde corresponde.
    return useMemo(
        () => (tipos.length > 0
            ? tipos
                .map(t => ({ valor: t.codigo, etiqueta: t.etiqueta }))
                .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, 'es', { sensitivity: 'base' }))
            : TIPOS_TRAMITE_RESPALDO),
        [tipos]);
}

// Los resultados de la atención que no terminaron en trámite: los únicos que pueden ir a "Volver a
// contactar". Mismo par que VistaDetalladaAtencion y VolverAContactarService (backend).
const RESULTADOS_SIN_TRAMITE = ['no viable', 'no quiso dejar carpeta'];
function esResultadoSinTramite(resultado) {
    const normalizado = String(resultado || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    return RESULTADOS_SIN_TRAMITE.includes(normalizado);
}

// onVolvioCliente (opcional): el turno está FINALIZADO y el cliente volvió al estudio. Lo abre el
// padre y no este modal, porque ModalReingresoCliente es un Modal de Bootstrap y quedaría debajo
// del overlay propio del turnero (z-index 4000).
// onVolverAContactar (opcional, solo el histórico): agregar la atención al listado "Volver a
// contactar" (V261), o ir al listado si ya está (enVolverAContactar). Mismo criterio: el modal de
// alta lo abre el padre. El turnero no lo pasa, así que ahí el botón no aparece.
export function ModalDetalleTurno({ turnoSel, onCerrar, onActualizado, onEliminado, onAsistioRegistrar, onVolvioCliente, onVolverAContactar, enVolverAContactar = false }) {
    const opcionesTipo = useTiposTurno();
    // Para firmar la observación con quién la dejó.
    const { user } = useAuth();
    const [subPanel, setSubPanel] = useState(null);
    const [verDetalleAnses, setVerDetalleAnses] = useState(false);
    const [estadoPendiente, setEstadoPendiente] = useState('');
    const [motivoInput, setMotivoInput] = useState('');
    const [reprogramarFecha, setReprogramarFecha] = useState('');
    const [reprogramarHorario, setReprogramarHorario] = useState('');
    const [horasDisponibles, setHorasDisponibles] = useState([]);
    const [cargandoHoras, setCargandoHoras] = useState(false);
    const [errorForm, setErrorForm] = useState('');
    // Aviso (no error) cuando el turno cambió mientras estaba abierto: ver avisoTurnoCambiado.
    const [avisoForm, setAvisoForm] = useState('');
    useEffect(() => { setAvisoForm(''); }, [turnoSel?.idTurno]);
    const [cambiandoEstado, setCambiandoEstado] = useState(false);
    const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
    const [confirmandoRevertir, setConfirmandoRevertir] = useState(false);
    const [editando, setEditando] = useState(false);
    const [formEdit, setFormEdit] = useState(null);
    const [guardandoEdit, setGuardandoEdit] = useState(false);
    const [errorEdit, setErrorEdit] = useState('');
    const [usuariosEdit, setUsuariosEdit] = useState([]);
    const [cargandoUsuariosEdit, setCargandoUsuariosEdit] = useState(false);
    const [referenciasDisponibles, setReferenciasDisponibles] = useState([]);

    useEffect(() => {
        DatosMaestrosService.getReferencias()
            .then(res => setReferenciasDisponibles(res.data || []))
            .catch(() => setReferenciasDisponibles([]));
    }, []);

    const iniciarEdicion = () => {
        setFormEdit({
            nombre: turnoSel.nombre || '',
            apellido: turnoSel.apellido || '',
            cuil: turnoSel.cuil || '',
            dni: turnoSel.dni || '',
            telefono: turnoSel.telefonoCliente || '',
            tipoTramite: turnoSel.tipoTramite || 'GENERAL',
            tipoTurno: turnoSel.tipoTurno || 'PRESENCIAL',
            idUsuarioAtiende: turnoSel.idUsuarioAtiende ? String(turnoSel.idUsuarioAtiende) : '',
            referencia: turnoSel.referencia || '',
            idReferencia: turnoSel.idReferencia || null,
            motivo: turnoSel.motivo || '',
        });
        setErrorEdit('');
        setEditando(true);
    };

    useEffect(() => {
        if (!editando || formEdit?.tipoTurno !== 'VIDEOLLAMADA' || usuariosEdit.length > 0) return;
        let cancelado = false;
        setCargandoUsuariosEdit(true);
        usuarioService.getParaSeleccion()
            .then(r => { if (!cancelado) setUsuariosEdit((r.data || []).filter(u => u.validado)); })
            .catch(() => { if (!cancelado) setUsuariosEdit([]); })
            .finally(() => { if (!cancelado) setCargandoUsuariosEdit(false); });
        return () => { cancelado = true; };
    }, [editando, formEdit?.tipoTurno, usuariosEdit.length]);

    const handleGuardarEdicion = async () => {
        if (!formEdit.nombre.trim() || !formEdit.apellido.trim()) {
            setErrorEdit('Nombre y apellido son obligatorios.');
            return;
        }
        setGuardandoEdit(true);
        setErrorEdit('');
        try {
            const actualizado = await editarDatosTurno(turnoSel.idTurno, {
                nombre: textoPersona(formEdit.nombre),
                apellido: textoPersona(formEdit.apellido),
                cuil: formEdit.cuil.trim() || null,
                dni: formEdit.dni.trim() || null,
                telefonoCliente: formEdit.telefono.trim() || null,
                tipoTramite: formEdit.tipoTramite,
                tipoTurno: formEdit.tipoTurno,
                idUsuarioAtiende: formEdit.tipoTurno === 'VIDEOLLAMADA' && formEdit.idUsuarioAtiende ? Number(formEdit.idUsuarioAtiende) : null,
                referencia: formEdit.referencia.trim() || null,
                idReferencia: formEdit.idReferencia || null,
                motivo: formEdit.motivo.trim() || null,
            });
            onActualizado(actualizado);
            setEditando(false);
        } catch (e) {
            setErrorEdit(e?.response?.data?.mensaje || 'Error al guardar los cambios.');
        } finally {
            setGuardandoEdit(false);
        }
    };

    const handleEliminar = async () => {
        try {
            await eliminarTurno(turnoSel.idTurno);
            onEliminado();
        } catch (e) {
            setErrorForm('Error al eliminar el turno.');
            setConfirmandoEliminar(false);
        }
    };

    useEffect(() => {
        if (subPanel !== 'reprogramar' || !reprogramarFecha) {
            setHorasDisponibles([]);
            return;
        }
        setCargandoHoras(true);
        obtenerHorariosDisponibles(reprogramarFecha)
            .then(horas => setHorasDisponibles(horas))
            .catch(() => setHorasDisponibles([]))
            .finally(() => setCargandoHoras(false));
    }, [subPanel, reprogramarFecha]);

    const [observacionNueva, setObservacionNueva] = useState('');
    const [guardandoObservacion, setGuardandoObservacion] = useState(false);

    // Anotar no cambia el estado: el turno sigue exactamente donde estaba.
    const handleAgregarObservacion = async () => {
        const texto = observacionNueva.trim();
        if (!texto || guardandoObservacion) return;
        setGuardandoObservacion(true);
        setErrorForm('');
        try {
            const actualizado = await agregarObservacionTurno(turnoSel.idTurno, texto, user?.id);
            onActualizado(actualizado);
            setObservacionNueva('');
        } catch (e) {
            setErrorForm(e?.response?.data?.mensaje || 'No se pudo guardar la observación.');
        } finally {
            setGuardandoObservacion(false);
        }
    };

    // Si el servidor rechaza un cambio, puede ser que el turno ya no esté como se ve (lo cambió el
    // bot u otra persona): se vuelve a pedir y, si cambió, se muestra el estado real con un aviso.
    const recuperarSiCambio = async () => {
        try {
            const actual = await obtenerTurno(turnoSel.idTurno);
            const aviso = avisoTurnoCambiado(turnoSel, actual);
            if (!aviso) return false;
            onActualizado(actual);
            setSubPanel(null);
            setConfirmandoRevertir(false);
            setErrorForm('');
            setAvisoForm(aviso);
            return true;
        } catch {
            return false;
        }
    };

    const handleCambiarEstado = async (nuevoEstado, extra = {}) => {
        setCambiandoEstado(true);
        setErrorForm('');
        setAvisoForm('');
        try {
            const actualizado = await cambiarEstado(turnoSel.idTurno, nuevoEstado, extra);
            onActualizado(actualizado);
            setSubPanel(null);
            if (nuevoEstado === 'ASISTIO') onAsistioRegistrar(actualizado);
        } catch (e) {
            if (await recuperarSiCambio()) return;
            setErrorForm(e?.response?.data?.mensaje || 'Error al cambiar el estado.');
        } finally {
            setCambiandoEstado(false);
        }
    };

    const handleRevertir = async () => {
        setCambiandoEstado(true);
        setErrorForm('');
        try {
            const actualizado = await revertirTurno(turnoSel.idTurno);
            onActualizado(actualizado);
            setSubPanel(null);
            setConfirmandoRevertir(false);
        } catch (e) {
            if (await recuperarSiCambio()) return;
            setErrorForm(e?.response?.data?.mensaje || 'No se pudo volver atrás.');
        } finally {
            setCambiandoEstado(false);
        }
    };

    const handleClickEstado = (valor) => {
        if (valor === 'NO_ASISTIO' || valor === 'CANCELADO') {
            setEstadoPendiente(valor);
            setMotivoInput('');
            setErrorForm('');
            setSubPanel('motivo');
        } else if (valor === 'REPROGRAMADO') {
            setEstadoPendiente('REPROGRAMADO');
            setReprogramarFecha('');
            setReprogramarHorario('');
            setErrorForm('');
            setSubPanel('reprogramar');
        } else {
            handleCambiarEstado(valor);
        }
    };

    const e = estiloEstado(turnoSel.estado);
    const leyendaFinal = leyendaEstadoFinal(turnoSel.estado, e.etiqueta);
    const esFinal = ESTADOS_FINALES.has(turnoSel.estado);
    // Qué va en la columna derecha: la atención si la hubo; si no, las acciones del turno abierto.
    const hayAtencion = !turnoSel.esTramiteExpres && tieneDatosAtencion(turnoSel);
    const accionesEnColumna = !hayAtencion && !esFinal && !turnoSel.esTramiteExpres;
    const columnaDerecha = hayAtencion || accionesEnColumna;

    // Acciones del turno abierto: reprogramarlo o cerrarlo. Mientras se completa el motivo o la
    // nueva fecha, el subpanel reemplaza a los botones. Mismos handlers que siempre.
    const renderAcciones = () => {
        // Se sacaron los botones de cambio de estado "genérico" (Creado/Pendiente/Confirmado):
        // desde el modal solo se puede reprogramar o finalizar el turno. REPROGRAMADO es una
        // acción repetible (mover de fecha/hora), no un estado destino excluyente: se muestra
        // incluso si el turno ya está reprogramado, para permitir reprogramarlo las veces que
        // haga falta.
        const disponibles = ESTADOS_TURNERO.filter(est =>
            (est.valor === 'REPROGRAMADO' || est.valor !== turnoSel.estado) && ACCIONES_MODAL.has(est.valor)
        );
        const enProgreso = disponibles.filter(est => !ESTADOS_FINALES.has(est.valor));
        const finales = disponibles.filter(est => ESTADOS_FINALES.has(est.valor));
        const botonEstado = (est) => (
            <button
                key={est.valor}
                disabled={cambiandoEstado}
                onClick={() => handleClickEstado(est.valor)}
                className={styles.botonEstadoGrande}
                style={{
                    background: est.bg, border: `1px solid ${est.border}`, color: est.text,
                    opacity: cambiandoEstado ? 0.5 : 1,
                }}
            >
                <i className={`bi ${est.icono}`} />
                {est.etiqueta}
            </button>
        );
        return (
            <section className={styles.detalleTarjeta}>
                <div className={styles.detalleTarjetaTitulo}>
                    <i className="bi bi-lightning-charge" />
                    Acciones del turno
                </div>
                {subPanel === 'motivo' ? (
                    <SubPanelMotivo
                        estadoPendiente={estadoPendiente}
                        motivoInput={motivoInput}
                        onChange={setMotivoInput}
                        cambiandoEstado={cambiandoEstado}
                        onVolver={() => setSubPanel(null)}
                        onConfirmar={handleCambiarEstado}
                        setError={setErrorForm}
                    />
                ) : subPanel === 'reprogramar' ? (
                    <SubPanelReprogramar
                        reprogramarFecha={reprogramarFecha}
                        setReprogramarFecha={setReprogramarFecha}
                        reprogramarHorario={reprogramarHorario}
                        setReprogramarHorario={setReprogramarHorario}
                        horasDisponibles={horasDisponibles}
                        cargandoHoras={cargandoHoras}
                        cambiandoEstado={cambiandoEstado}
                        onVolver={() => setSubPanel(null)}
                        onConfirmar={handleCambiarEstado}
                        setError={setErrorForm}
                    />
                ) : (
                    <div className={styles.detalleAcciones}>
                        {enProgreso.length > 0 && (
                            <div>
                                <div className={styles.detalleAccionesRotulo}>Mover el turno</div>
                                <div className="d-flex flex-wrap gap-2">
                                    {enProgreso.map(botonEstado)}
                                </div>
                            </div>
                        )}
                        {finales.length > 0 && (
                            <div>
                                <div className={styles.detalleAccionesRotulo}>
                                    <i className="bi bi-lock-fill me-1" />
                                    Finalizar turno
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {finales.map(botonEstado)}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </section>
        );
    };

    return (
        <>
            {/* Barra superior clara (modalHeaderClaro), solo con el título y la X: quién es el
                cliente va en la primera tarjeta del cuerpo, con el mismo formato que el resto. El
                resto de los modales del turnero conserva su header azul. */}
            <div className={`${styles.modalHeader} ${styles.modalHeaderClaro}`}>
                <div className={styles.modalHeaderIzq}>
                    <i className="bi bi-calendar2-check" />
                    <h5 className={styles.modalTitulo}>Detalle del turno</h5>
                </div>
                <div className={styles.modalHeaderAcciones}>
                    {/* Editar está en el pie, junto a Eliminar y Cerrar. */}
                    <button type="button" className={styles.modalHeaderBtn} onClick={onCerrar} title="Cerrar">
                        <i className="bi bi-x-lg" />
                    </button>
                </div>
            </div>

            <div className={`${styles.modalBody} ${styles.detalleBody}`}>
            {/* Quién es: nombre, trámite y estado. Va también en modo edición. */}
            <section className={`${styles.detalleTarjeta} ${styles.detalleIdentidad}`}>
                <span className={styles.modalHeaderAvatar}>
                    <i className="bi bi-person-fill" />
                </span>
                <div className={styles.modalHeaderTexto}>
                    {/* "TORRES, JORGE - Jubilación". En el exprés, en lugar del tipo de trámite va
                        por qué volvió. */}
                    <h5 className={styles.modalHeaderNombre}>
                        {apellidoNombre(turnoSel.apellido, turnoSel.nombre)}
                        <span className={styles.detalleIdentidadTramite}>
                            {' - '}
                            {turnoSel.esTramiteExpres
                                ? (etiquetaMotivoExpres(turnoSel.expresMotivo, turnoSel.expresMotivoDetalle) || 'Trámite exprés')
                                : etiquetaTipo(turnoSel.tipoTramite)}
                        </span>
                    </h5>
                    {turnoSel.numeroIdentificacion && (
                        <span className={styles.modalHeaderSubtitulo}>Turno #{turnoSel.numeroIdentificacion}</span>
                    )}
                    {/* En el exprés no van chips: el estado siempre es Finalizado y no dice nada;
                        lo que importa lo explica el aviso de abajo. */}
                    {!turnoSel.esTramiteExpres && (
                    <div className={styles.modalHeaderEstadoFila}>
                        <span className={styles.modalHeaderEstadoChip} style={estiloChip(e)}>
                            <span className={styles.modalHeaderEstadoDot} style={{ background: e.border }} />
                            {e.etiqueta}
                        </span>
                        {turnoSel.esSobreturno && !turnoSel.esReingreso && (
                            <span className={styles.modalHeaderEstadoChip} style={estiloChip(ESTILO_SOBRETURNO)}>
                                <i className="bi bi-exclamation-diamond-fill" />
                                Sobreturno
                            </span>
                        )}
                        {turnoSel.esTramiteExpres && (
                            <span className={styles.modalHeaderEstadoChip} style={estiloChip(ESTILO_EXPRES)}
                                  title="Solo entregó o retiró documentación">
                                <i className="bi bi-lightning-charge-fill" />
                                Trámite exprés
                            </span>
                        )}
                        {turnoSel.esReingreso && !turnoSel.esTramiteExpres && (
                            <span className={styles.modalHeaderEstadoChip} style={estiloChip(ESTILO_CHIP_REINGRESO)}
                                  title="Volvió al estudio después de una atención ya finalizada">
                                <i className="bi bi-arrow-repeat" />
                                {turnoSel.prioridadReingreso ? 'Reingreso · Prioridad' : 'Reingreso'}
                            </span>
                        )}
                        {/* Los datos de este turno se editan en el trámite, no acá. */}
                        {turnoSel.modalidad === 'ANSES' && (
                            <span className={styles.modalHeaderEstadoChip} style={estiloChip(ESTILO_CHIP_ANSES)}
                                  title={esSinPresentacion(turnoSel.tipoTurnoAnses)
                                      ? metaTipoTurnoAnses(turnoSel.tipoTurnoAnses).detalleTurnero
                                      : 'Citación generada por un turno en ANSES'}>
                                <i className={`bi ${esSinPresentacion(turnoSel.tipoTurnoAnses) ? metaTipoTurnoAnses(turnoSel.tipoTurnoAnses).icono : 'bi-building'}`} />
                                {esSinPresentacion(turnoSel.tipoTurnoAnses)
                                    ? ` ${metaTipoTurnoAnses(turnoSel.tipoTurnoAnses).enTurnero}`
                                    : ` ANSES ${turnoSel.horaTurnoAnses ? `· turno ${turnoSel.horaTurnoAnses}` : ''}`}
                            </span>
                        )}
                    </div>
                    )}
                </div>
                {/* El exprés explica qué es al pie de la tarjeta del nombre, en lugar de los chips. */}
                {turnoSel.esTramiteExpres && <AvisoTramiteExpres className={styles.detalleIdentidadAviso} />}
            </section>
            {editando ? (
                <div style={{ marginBottom: 16 }}>
                    <div className="row g-2 mb-3">
                        <div className="col-6">
                            <label className={`form-label fw-semibold ${styles.formLabel}`}>Nombre *</label>
                            <input className="form-control" value={formEdit.nombre} onChange={e => setFormEdit(f => ({ ...f, nombre: e.target.value }))} />
                        </div>
                        <div className="col-6">
                            <label className={`form-label fw-semibold ${styles.formLabel}`}>Apellido *</label>
                            <input className="form-control" value={formEdit.apellido} onChange={e => setFormEdit(f => ({ ...f, apellido: e.target.value }))} />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>Tipo de trámite</label>
                        <select
                            className="form-select"
                            value={formEdit.tipoTramite}
                            onChange={e => setFormEdit(f => ({ ...f, tipoTramite: e.target.value }))}
                        >
                            {opcionesTipo.map(t => (
                                <option key={t.valor} value={t.valor}>{t.etiqueta}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-3">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>Modalidad</label>
                        <select
                            className="form-select"
                            value={formEdit.tipoTurno}
                            onChange={e => setFormEdit(f => ({
                                ...f,
                                tipoTurno: e.target.value,
                                idUsuarioAtiende: e.target.value === 'PRESENCIAL' ? '' : f.idUsuarioAtiende,
                            }))}
                        >
                            {TIPOS_TURNO.map(t => (
                                <option key={t.valor} value={t.valor}>{t.etiqueta}</option>
                            ))}
                        </select>
                    </div>

                    {formEdit.tipoTurno === 'VIDEOLLAMADA' && (
                        <div className="mb-3">
                            <label className={`form-label fw-semibold ${styles.formLabel}`}>¿Quién atiende la videollamada? <span className="text-muted fw-normal">(opcional, se puede definir después)</span></label>
                            {cargandoUsuariosEdit ? (
                                <div style={{ fontSize: '0.85rem', color: '#64748b', paddingTop: 8 }}>
                                    <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 1 }} /> Cargando usuarios…
                                </div>
                            ) : (
                                <select
                                    className="form-select"
                                    value={formEdit.idUsuarioAtiende}
                                    onChange={e => setFormEdit(f => ({ ...f, idUsuarioAtiende: e.target.value }))}
                                >
                                    <option value="">Seleccionar usuario…</option>
                                    {usuariosEdit.map(u => (
                                        <option key={u.id} value={u.id}>{u.nombre}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    <div className="mb-3">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>CUIL</label>
                        <input className="form-control" value={formEdit.cuil} onChange={e => setFormEdit(f => ({ ...f, cuil: e.target.value }))} />
                    </div>

                    <div className="mb-3">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>Teléfono</label>
                        <input className="form-control" value={formEdit.telefono} onChange={e => setFormEdit(f => ({ ...f, telefono: e.target.value }))} />
                    </div>

                    <div className="mb-3">
                        <ComboboxDatosMaestros
                            label="Referencia"
                            placeholder="Seleccione o escriba el canal de origen..."
                            opciones={referenciasDisponibles}
                            valor={formEdit.referencia}
                            onChange={(nuevoNombre) => setFormEdit(f => ({ ...f, ...resolverCambioReferencia(nuevoNombre, referenciasDisponibles) }))}
                            onCrearNuevo={(nuevaRef) => {
                                setReferenciasDisponibles(prev => [...prev, nuevaRef]);
                                setFormEdit(f => ({ ...f, referencia: nuevaRef.nombre, idReferencia: nuevaRef.id }));
                            }}
                            tipo="referencia"
                        />
                    </div>

                    <div className="mb-3">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>Motivo de consulta</label>
                        <textarea
                            className="form-control"
                            rows={2}
                            placeholder="Por qué viene el cliente..."
                            value={formEdit.motivo}
                            onChange={e => setFormEdit(f => ({ ...f, motivo: e.target.value }))}
                        />
                    </div>

                    {errorEdit && <div className="alert alert-danger py-2 mb-3">{errorEdit}</div>}

                    <div className="d-flex gap-2">
                        <button className="rec-btn-secondary flex-fill" onClick={() => setEditando(false)} disabled={guardandoEdit}>
                            Cancelar
                        </button>
                        <button className="rec-btn-primary flex-fill" onClick={handleGuardarEdicion} disabled={guardandoEdit}>
                            {guardandoEdit ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1" />}
                            Guardar cambios
                        </button>
                    </div>
                </div>
            ) : (
            <>
            {/* De qué cliente es este turno. Va primero y explicado: más abajo pasaba por un
                buscador cualquiera y nadie entendía para qué estaba. Es corregible desde cualquier
                pantalla que abra el modal (turnero, panel diario, histórico). */}
            <section className={`${styles.detalleTarjeta} ${styles.detalleTarjetaCliente}`}>
                <div className={`${styles.detalleTarjetaTitulo} mb-1`}>
                    <i className="bi bi-person-vcard" />
                    {turnoSel.cuilCliente ? 'Cliente del estudio vinculado' : 'Vincular con un cliente del estudio'}
                </div>
                <p className={styles.detalleTarjetaAyuda}>
                    {turnoSel.cuilCliente
                        ? 'Este turno está asociado a la ficha de este cliente: la visita aparece en su historial de turnos. Si no es esa persona, desvinculalo.'
                        : 'Si la persona ya tiene ficha en el estudio, buscala por CUIL, nombre o apellido y asociá el turno: la visita va a aparecer en su historial de turnos. Si todavía no es cliente, dejalo sin vincular.'}
                </p>
                <VincularClienteTurno turno={turnoSel} onCambio={onActualizado} compacto />
            </section>

            {/* Desenlace (reprogramado / cancelado / no asistió): lo primero del turno que hay que ver. */}
            {!turnoSel.esTramiteExpres && <CalloutEstado turnoSel={turnoSel} />}

            {/* El trámite exprés (V249) tiene su propia vista: no hubo agenda ni atención. */}
            {turnoSel.esTramiteExpres ? (
                <section className={styles.detalleTarjeta}>
                    <DetalleTramiteExpres turno={turnoSel} />
                </section>
            ) : (
                // Dos columnas: a la izquierda el turno (cuándo, cómo, de dónde vino), a la
                // derecha qué pasó con él (la atención) o, si todavía no se atendió, qué se
                // puede hacer. Si no hay ni una cosa ni la otra, va una sola columna.
                <div className={`${styles.detalleGrid} ${columnaDerecha ? '' : styles.detalleGridUna}`}>
                    <section className={styles.detalleTarjeta}>
                        <div className={styles.detalleTarjetaTitulo}>
                            <i className="bi bi-calendar2-week" />
                            Datos del turno
                        </div>
                        <div className={`${styles.detalleKV} ${columnaDerecha ? '' : styles.detalleKVAncho}`}>
                            <DatoTurno etiqueta="Fecha" valor={`${formatearFechaLarga(turnoSel.fechaTurno)} · ${turnoSel.horario}`} />
                            <DatoTurno
                                etiqueta="Modalidad"
                                valor={`${etiquetaTipoTurno(turnoSel.tipoTurno)}${turnoSel.tipoTurno === 'VIDEOLLAMADA' && turnoSel.usuarioAtiendeNombre ? ` · ${turnoSel.usuarioAtiendeNombre}` : ''}`}
                            />
                            <DatoTurno etiqueta="Canal" valor={formatearCanal(turnoSel.canalOrigen)} />
                            {turnoSel.atencion && <DatoTurno etiqueta="Área" valor={etiquetaArea(turnoSel.atencion)} />}
                            <DatoTurno etiqueta="Fecha de creación" valor={formatearFechaHoraCreacion(turnoSel.creadoEn)} />
                            {/* Se muestra siempre, incluido el "no registrado": que el dato falte
                                es información útil, y decir "No" cuando nadie preguntó sería inventar. */}
                            <DatoTurno
                                etiqueta="Primera vez"
                                valor={turnoSel.esPrimeraVez == null
                                    ? 'No registrado'
                                    : turnoSel.esPrimeraVez ? 'Sí, primera vez' : 'No, ya había venido'}
                                apagado={turnoSel.esPrimeraVez == null}
                            />
                            {turnoSel.telefonoCliente && <DatoTurno etiqueta="Teléfono" valor={turnoSel.telefonoCliente} />}
                            {turnoSel.cuil && <DatoTurno etiqueta="CUIL" valor={turnoSel.cuil} />}
                            {turnoSel.dni && <DatoTurno etiqueta="DNI" valor={turnoSel.dni} />}
                            {turnoSel.referencia && <DatoTurno etiqueta="Referencia" valor={turnoSel.referencia} />}
                            {turnoSel.tieneCarpeta && <DatoTurno etiqueta="Carpeta" valor="Ya tiene carpeta" />}
                        </div>
                        {/* motivoEstado y fechaReprogramada no van acá: los muestra CalloutEstado. */}
                        {turnoSel.motivo && (
                            <div className={styles.detalleMotivo}>
                                <span className={styles.detalleKVEtiqueta}>Motivo de consulta</span>
                                <p>{turnoSel.motivo}</p>
                            </div>
                        )}
                        {turnoSel.idTurnoAnses && (
                            <button
                                type="button"
                                className={`btn btn-outline-primary btn-sm w-100 ${styles.detalleBotonAnses}`}
                                onClick={() => setVerDetalleAnses(true)}
                            >
                                <i className="bi bi-building me-1" />Ver detalle del turno ANSES (poder, documentación, UDAI)
                            </button>
                        )}
                    </section>

                    {hayAtencion
                        ? <PanelAtencion turnoSel={turnoSel} />
                        : accionesEnColumna && renderAcciones()}
                </div>
            )}

            <ModalDetalleTurnoAnses
                idTurnoAnses={verDetalleAnses ? turnoSel.idTurnoAnses : null}
                onClose={() => setVerDetalleAnses(false)}
            />

            {/* Turno con atención y todavía abierto: las acciones van debajo, a lo ancho. */}
            {hayAtencion && !esFinal && !turnoSel.esTramiteExpres && renderAcciones()}

            {/* Observaciones: dejar constancia sin mover el turno de estado. Antes, para anotar
                "lo llamé y no contesta" había que cambiarle el estado o no anotarlo. */}
            <section className={styles.detalleTarjeta}>
                <div className={styles.detalleTarjetaTitulo}>
                    <i className="bi bi-journal-text" />
                    Observaciones
                </div>
                <BitacoraTimeline observaciones={turnoSel.observaciones} />
                <div className="input-group">
                    <input
                        className="form-control"
                        placeholder="Ej: lo llamé y no contesta"
                        value={observacionNueva}
                        maxLength={500}
                        onChange={(e) => setObservacionNueva(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAgregarObservacion()}
                    />
                    <button
                        className="btn btn-outline-primary"
                        disabled={!observacionNueva.trim() || guardandoObservacion}
                        onClick={handleAgregarObservacion}
                    >
                        <i className="bi bi-plus-lg me-1" />
                        {guardandoObservacion ? 'Guardando...' : 'Anotar'}
                    </button>
                </div>
            </section>

            {avisoForm && <div className="alert alert-warning py-2 mb-0"><i className="bi bi-info-circle me-2" />{avisoForm}</div>}
            {errorForm && <div className="alert alert-danger py-2 mb-0">{errorForm}</div>}
            </>
            )}
            </div>

            {/* Pie fijo: lo destructivo a la izquierda, la navegación a la derecha. Las
                confirmaciones (eliminar / volver atrás) se abren acá mismo, en lugar del pie. En
                modo edición no hay pie: el formulario tiene sus propios botones. */}
            {!editando && (
                <div className={`${styles.modalFooter} ${styles.detalleFooter}`}>
                    {confirmandoEliminar ? (
                        <div className={styles.detalleFooterConfirmacion}>
                            <span className={styles.detalleFooterConfirmacionTexto}>
                                <i className="bi bi-exclamation-triangle-fill me-2" />
                                ¿Eliminar este turno? Esta acción no se puede deshacer.
                            </span>
                            <div className="d-flex gap-2">
                                <button className="btn btn-outline-secondary btn-sm" onClick={() => setConfirmandoEliminar(false)}>
                                    Cancelar
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={handleEliminar}>
                                    <i className="bi bi-trash me-1" />
                                    Confirmar eliminación
                                </button>
                            </div>
                        </div>
                    ) : confirmandoRevertir ? (
                        <div className={`${styles.detalleFooterConfirmacion} ${styles.detalleFooterConfirmacionNeutra}`}>
                            <span className={styles.detalleFooterConfirmacionTexto}>
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                ¿Volver el turno al estado anterior? Se usa para corregir un error (ej: se marcó Asistió sin querer).
                            </span>
                            <div className="d-flex gap-2">
                                <button className="btn btn-outline-secondary btn-sm" onClick={() => setConfirmandoRevertir(false)} disabled={cambiandoEstado}>
                                    Cancelar
                                </button>
                                <button className="btn btn-primary btn-sm" onClick={handleRevertir} disabled={cambiandoEstado}>
                                    {cambiandoEstado ? 'Volviendo…' : 'Sí, volver atrás'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Estado cerrado: a la izquierda, como contexto de los botones. */}
                            {esFinal && !turnoSel.esTramiteExpres ? (
                                <span className={styles.detalleFooterLeyenda} title={leyendaFinal.ayuda}>
                                    <i className="bi bi-lock-fill" style={{ color: e.text }} />
                                    {leyendaFinal.titulo}
                                </span>
                            ) : <span />}
                            {/* Todos los botones del pie con la misma forma y tamaño (detalleFooterBtn);
                                solo Cerrar va relleno. Eliminar, Editar y Cerrar quedan juntos al final. */}
                            <div className={styles.detalleFooterDerecha}>
                                {/* Al exprés no se le cambia el estado ni se registra un retorno: nace terminado. */}
                                {esFinal && !turnoSel.esTramiteExpres && (
                                    <>
                                        {turnoSel.estado === 'FINALIZADO' && onVolverAContactar && esResultadoSinTramite(turnoSel.resultadoAtencion) && (
                                            <button
                                                type="button"
                                                className={styles.detalleFooterBtn}
                                                onClick={() => onVolverAContactar(turnoSel)}
                                                title={enVolverAContactar ? 'Ya está en el listado: ir al listado' : 'Agregar al listado para avisarle más adelante'}
                                            >
                                                <i className="bi bi-telephone-outbound" />
                                                {enVolverAContactar ? 'En Volver a contactar' : 'Volver a contactar'}
                                            </button>
                                        )}
                                        {turnoSel.estado === 'FINALIZADO' && !turnoSel.vuelvePorPlan && onVolvioCliente && (
                                            <button
                                                type="button"
                                                className={styles.detalleFooterBtn}
                                                onClick={() => onVolvioCliente(turnoSel)}
                                            >
                                                <i className="bi bi-arrow-repeat" />
                                                Volvió cliente
                                            </button>
                                        )}
                                        {/* Salió hacia ANSES por el plan de pago: su retorno es abrir el plan
                                            (ModalReingresoCliente ofrece solo esa opción). */}
                                        {turnoSel.vuelvePorPlan && onVolvioCliente && (
                                            <button
                                                type="button"
                                                className={styles.detalleFooterBtn}
                                                style={{ borderColor: '#10b981', color: '#047857' }}
                                                onClick={() => onVolvioCliente(turnoSel)}
                                            >
                                                <i className="bi bi-arrow-return-left" />
                                                Volvió de ANSES
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className={styles.detalleFooterBtn}
                                            onClick={() => setConfirmandoRevertir(true)}
                                        >
                                            <i className="bi bi-arrow-counterclockwise" />
                                            Volver atrás
                                        </button>
                                        <span className={styles.detalleFooterSeparador} aria-hidden="true" />
                                    </>
                                )}
                                <button
                                    type="button"
                                    className={`${styles.detalleFooterBtn} ${styles.detalleFooterBtnPeligro}`}
                                    onClick={() => setConfirmandoEliminar(true)}
                                >
                                    <i className="bi bi-trash" />
                                    Eliminar
                                </button>
                                {/* El exprés no tiene datos de agenda que editar: se corrige eliminándolo. */}
                                {!turnoSel.esTramiteExpres && (
                                    <button type="button" className={styles.detalleFooterBtn} onClick={iniciarEdicion}>
                                        <i className="bi bi-pencil" />
                                        Editar
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`${styles.detalleFooterBtn} ${styles.detalleFooterBtnPrimario}`}
                                    onClick={onCerrar}
                                >
                                    <i className="bi bi-check2" />
                                    Cerrar
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
}

// ── ModalCrearTurno ──────────────────────────────────────────────────────────

export function ModalCrearTurno({
    celdaSel, onCerrar, onCreado, valoresIniciales, modoEspontaneo = false,
    // clienteInicial: vincula directamente al cliente (mismo camino que elegirlo del buscador de
    // abajo), en vez de solo prellenar los campos de texto — así el turno queda con el mismo dato
    // "cliente del estudio" que si se hubiera buscado a mano.
    // fechaSugerida/horaSugerida: precargan fecha y horario pero quedan editables (a diferencia de
    // celdaSel, que los deja fijos porque representa una celda de la grilla ya elegida a propósito).
    clienteInicial, fechaSugerida, horaSugerida,
    // idEventoCitado: el turno es la citación al estudio para una junta médica o pericia (V254):
    // queda vinculado a ese evento y el recordatorio de WhatsApp usa la hora de este turno.
    idEventoCitado = null,
}) {
    const opcionesTipo = useTiposTurno();
    const [form, setForm] = useState(() => ({ ...FORM_VACIO, ...valoresIniciales }));
    const [fechaSel, setFechaSel] = useState(() => celdaSel ? fechaToISO(celdaSel.fecha) : (fechaSugerida || fechaToISO(new Date())));
    const [horarioSel, setHorarioSel] = useState(celdaSel ? celdaSel.horario : '');
    const sugerenciaHorarioUsada = useRef(false);
    const [horariosPosibles, setHorariosPosibles] = useState([]);
    const [cargandoHoras, setCargandoHoras] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [errorForm, setErrorForm] = useState('');
    const [horarioCompleto, setHorarioCompleto] = useState(null); // { ocupados, maximo } cuando el slot ya está lleno
    // { fecha, motivo } cuando el día es feriado o está marcado como no laborable. El mostrador
    // puede agendar igual (a veces se cita a alguien un día cerrado a propósito), pero confirmando.
    const [diaNoLaborable, setDiaNoLaborable] = useState(null);
    // Teléfono y referencia no son obligatorios (hay quien no deja número o no sabe cómo llegó),
    // pero sin ellos el cliente queda sin contacto y sin referencia en la ficha (oct-2026: 5 altas
    // sin referencia en dos semanas). Se pide confirmación UNA vez por turno: después de confirmar,
    // los reintentos (sobreturno, día no laborable) no vuelven a preguntar.
    const [datosFaltantes, setDatosFaltantes] = useState(null); // ['el teléfono', 'la referencia']
    const [faltantesConfirmados, setFaltantesConfirmados] = useState(false);
    const [referenciasDisponibles, setReferenciasDisponibles] = useState([]);
    // Mientras el operador no haya elegido el área a mano, la sugerencia se recalcula sola
    // cada vez que cambia el tipo de trámite (solo aplica en modo espontáneo, único caso
    // donde el selector de área es visible).
    const [areaTocada, setAreaTocada] = useState(false);

    // Ingreso espontáneo: el que llega sin turno suele ser un cliente que ya existe, así que
    // antes de tipear todo de nuevo se lo puede buscar y traer sus datos.
    const [busquedaCliente, setBusquedaCliente] = useState('');
    const [clientesEncontrados, setClientesEncontrados] = useState([]);
    const [buscandoCliente, setBuscandoCliente] = useState(false);
    const [clienteVinculado, setClienteVinculado] = useState(null);
    const debounceCliente = useRef(null);

    // Búsqueda en vivo con el mismo debounce de 350ms que usa SelectorReferenciaCliente.
    useEffect(() => {
        if (debounceCliente.current) clearTimeout(debounceCliente.current);
        if (!busquedaCliente.trim()) {
            setClientesEncontrados([]);
            setBuscandoCliente(false);
            return undefined;
        }
        setBuscandoCliente(true);
        debounceCliente.current = setTimeout(async () => {
            try {
                const encontrados = await buscarClientes(busquedaCliente.trim());
                setClientesEncontrados(Array.isArray(encontrados) ? encontrados.slice(0, 6) : []);
            } catch (e) {
                setClientesEncontrados([]);
            } finally {
                setBuscandoCliente(false);
            }
        }, 350);
        return () => clearTimeout(debounceCliente.current);
    }, [busquedaCliente]);

    const vincularCliente = (cliente) => {
        setClienteVinculado(cliente);
        setForm(f => ({
            ...f,
            nombre: cliente.nombre || '',
            apellido: cliente.apellido || '',
            cuil: cliente.cuil || '',
            telefono: cliente.telefonoPropio || f.telefono,
            // La referencia del turno es la misma que la del cliente: de parte de quién vino.
            // Se arrastra para no volver a elegirla y para que el reporte por canal no quede
            // con turnos sin referencia de gente que sí la tiene cargada.
            referencia: cliente.referencia || f.referencia,
            idReferencia: cliente.idReferencia ?? f.idReferencia
        }));
        setBusquedaCliente('');
        setClientesEncontrados([]);
    };

    const desvincularCliente = () => {
        setClienteVinculado(null);
        setForm(f => ({ ...f, nombre: '', apellido: '', cuil: '', telefono: '', referencia: '', idReferencia: null }));
    };

    // Vincula el cliente ya conocido (ej. la ficha de un trámite) apenas se abre el modal, por el
    // mismo camino que si se lo hubiera buscado a mano. Un solo cliente instanciado por apertura
    // del modal (se desmonta al cerrar), así que alcanza con que corra una vez al montar.
    useEffect(() => {
        if (clienteInicial) vincularCliente(clienteInicial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        DatosMaestrosService.getReferencias()
            .then(res => setReferenciasDisponibles(res.data || []))
            .catch(() => setReferenciasDisponibles([]));
    }, []);
    const [usuarios, setUsuarios] = useState([]);
    const [cargandoUsuarios, setCargandoUsuarios] = useState(false);

    // Se carga recién cuando hace falta (modalidad videollamada), no en cada apertura del modal.
    useEffect(() => {
        if (form.tipoTurno !== 'VIDEOLLAMADA' || usuarios.length > 0) return;
        let cancelado = false;
        setCargandoUsuarios(true);
        usuarioService.getParaSeleccion()
            .then(r => { if (!cancelado) setUsuarios((r.data || []).filter(u => u.validado)); })
            .catch(() => { if (!cancelado) setUsuarios([]); })
            .finally(() => { if (!cancelado) setCargandoUsuarios(false); });
        return () => { cancelado = true; };
    }, [form.tipoTurno, usuarios.length]);

    // Sin celda preseleccionada (botón "Nuevo turno"): traemos todos los bloques horarios
    // configurados (sin filtrar por ocupación) para poder elegir uno lleno y ofrecer sobreturno.
    // La config se pide una sola vez; los bloques se recalculan aparte (efecto de abajo) cada
    // vez que cambia la fecha elegida, porque cada día de la semana tiene su propio horario.
    const [configTurneroCrear, setConfigTurneroCrear] = useState(null);
    useEffect(() => {
        if (celdaSel) return;
        let cancelado = false;
        setCargandoHoras(true);
        obtenerConfigTurnero()
            .then(cfg => { if (!cancelado) setConfigTurneroCrear(cfg); })
            .catch(() => setConfigTurneroCrear(null))
            .finally(() => { if (!cancelado) setCargandoHoras(false); });
        return () => { cancelado = true; };
    }, [celdaSel]);

    // Antes esto se calculaba una sola vez con la fecha de HOY y nunca se actualizaba al elegir
    // otro día en el formulario: si hoy era lunes y se elegía un viernes (que puede tener su
    // propio horario, ej. hasta las 12), la grilla seguía mostrando los bloques del lunes.
    useEffect(() => {
        if (celdaSel || !configTurneroCrear || !fechaSel) return;
        const bloques = generarBloquesHorarios(configTurneroCrear, isoAFecha(fechaSel));
        setHorariosPosibles(bloques);
        setHorarioSel(actual => {
            // Un horario elegido para otro día (ej. lunes 14:30) puede no existir en el día nuevo
            // (ej. viernes, que cierra a las 12): sin esto quedaba seleccionado igual y se mandaba
            // un horario inválido para esa fecha al confirmar.
            if (actual && bloques.includes(actual)) return actual;
            // Preselecciona el bloque más cercano a la hora sugerida (ej. la de un evento ya
            // cargado, que puede no caer justo en un bloque del turnero), sin bloquear el select:
            // sigue siendo una sugerencia editable, no una celda fija como celdaSel. Solo se aplica
            // una vez — si el operador después cambia de fecha, no le vuelve a pisar la elección.
            if (!sugerenciaHorarioUsada.current && horaSugerida && bloques.length > 0) {
                sugerenciaHorarioUsada.current = true;
                return bloqueMasCercano(bloques, horaSugerida);
            }
            return actual && !bloques.includes(actual) ? '' : actual;
        });
    }, [celdaSel, configTurneroCrear, fechaSel, horaSugerida]);

    // Bloque horario vigente ahora mismo, usado por el modo espontáneo: el turno se registra
    // en el bloque actual de la grilla (o el más cercano si el estudio todavía no abrió / ya
    // cerró), y siempre como sobreturno.
    const bloqueActual = useMemo(() => {
        if (horariosPosibles.length === 0) return '';
        const ahora = new Date();
        const horaActual = `${String(ahora.getHours()).padStart(2, '0')}:${String(ahora.getMinutes()).padStart(2, '0')}`;
        const candidatos = horariosPosibles.filter((h) => h <= horaActual);
        return candidatos.length > 0 ? candidatos[candidatos.length - 1] : horariosPosibles[0];
    }, [horariosPosibles]);

    // Auto-sugerencia de área según el trámite elegido, hasta que el operador la toque a mano.
    useEffect(() => {
        if (!modoEspontaneo || areaTocada) return;
        setForm(f => ({ ...f, atencion: derivarAtencion(f.tipoTramite) }));
    }, [form.tipoTramite, modoEspontaneo, areaTocada]);

    // Cuánta gente hay ya en este bloque. En el ingreso espontáneo el turno entra siempre como
    // sobreturno, así que el aviso de "horario completo" nunca aparece: sin esto, recepción
    // carga a alguien sobre un bloque lleno sin enterarse.
    const [ocupacion, setOcupacion] = useState(null);
    useEffect(() => {
        if (!modoEspontaneo || !bloqueActual) return;
        let cancelado = false;
        obtenerOcupacionBloque(fechaToISO(new Date()), bloqueActual)
            .then(o => { if (!cancelado) setOcupacion(o); })
            .catch(() => { if (!cancelado) setOcupacion(null); });
        return () => { cancelado = true; };
    }, [modoEspontaneo, bloqueActual]);

    const construirPayload = (comoSobreturno, confirmarDiaNoLaborable = false) => ({
        confirmarDiaNoLaborable,
        nombre: textoPersona(form.nombre),
        apellido: textoPersona(form.apellido),
        cuil: form.cuil.trim() || null,
        telefonoCliente: form.telefono.trim() || null,
        tipoTramite: form.tipoTramite,
        atencion: form.atencion || null,
        tipoTurno: form.tipoTurno,
        idUsuarioAtiende: form.tipoTurno === 'VIDEOLLAMADA' && form.idUsuarioAtiende ? Number(form.idUsuarioAtiende) : null,
        fechaTurno: modoEspontaneo ? fechaToISO(new Date()) : (celdaSel ? fechaToISO(celdaSel.fecha) : fechaSel),
        horario: modoEspontaneo ? bloqueActual : (celdaSel ? celdaSel.horario : horarioSel),
        comoSobreturno: modoEspontaneo ? true : comoSobreturno,
        referencia: form.referencia.trim() || null,
        idReferencia: form.idReferencia || null,
        tieneCarpeta: !!form.tieneCarpeta,
        // '' significa que no se preguntó: viaja como null, no como false.
        esPrimeraVez: form.esPrimeraVez === '' ? null : form.esPrimeraVez === 'SI',
        motivo: form.motivo.trim() || null,
        idEventoCitado,
    });

    const handleGuardar = async (comoSobreturno = false, confirmarDiaNoLaborable = false, faltantesOk = faltantesConfirmados) => {
        // El teléfono no se exige: la columna lo admite nulo y el backend tampoco lo pide. Hay
        // clientes que no dejan número, y obligarlo llevaba a inventar uno.
        if (!form.nombre.trim() || !form.apellido.trim()) {
            setErrorForm('Nombre y apellido son obligatorios.');
            return;
        }
        if (modoEspontaneo) {
            if (!form.atencion) {
                setErrorForm('Seleccioná el área de atención.');
                return;
            }
            if (!bloqueActual) {
                setErrorForm('No hay ningún horario configurado para registrar el ingreso.');
                return;
            }
        } else if (!celdaSel && (!fechaSel || !horarioSel)) {
            setErrorForm('Fecha y horario son obligatorios.');
            return;
        }
        if (!faltantesOk) {
            const faltan = [];
            if (!form.telefono.trim()) faltan.push('el teléfono');
            if (!form.idReferencia && !form.referencia.trim()) faltan.push('la referencia');
            if (faltan.length > 0) {
                setDatosFaltantes(faltan);
                return;
            }
        }
        setGuardando(true);
        setErrorForm('');
        try {
            const creado = await crearTurno(construirPayload(comoSobreturno, confirmarDiaNoLaborable));
            onCreado(creado);
        } catch (e) {
            if (e?.response?.status === 409 && e?.response?.data?.codigo === 'HORARIO_COMPLETO') {
                setHorarioCompleto({ ocupados: e.response.data.ocupados, maximo: e.response.data.maximo });
            } else if (e?.response?.status === 409 && e?.response?.data?.codigo === 'DIA_NO_LABORABLE') {
                setDiaNoLaborable({ fecha: e.response.data.fecha, motivo: e.response.data.motivo });
            } else {
                setErrorForm(e?.response?.data?.mensaje || 'Error al guardar el turno.');
            }
        } finally {
            setGuardando(false);
        }
    };

    if (datosFaltantes) {
        return (
            <>
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderIzq}>
                        <i className="bi bi-exclamation-circle" />
                        <h5 className={styles.modalTitulo}>Faltan datos del cliente</h5>
                    </div>
                    <div className={styles.modalHeaderAcciones}>
                        <button type="button" className={styles.modalHeaderBtn} onClick={onCerrar}>
                            <i className="bi bi-x-lg" />
                        </button>
                    </div>
                </div>
                <div className={styles.modalBody}>
                    <p style={{ fontSize: '0.9rem', color: '#334155' }}>
                        El turno no tiene <strong>{datosFaltantes.join(' ni ')}</strong>.
                    </p>
                    <p style={{ fontSize: '0.88rem', color: '#475569' }}>
                        {datosFaltantes.includes('el teléfono') && 'Sin teléfono no se le puede avisar ni recordar el turno. '}
                        {datosFaltantes.includes('la referencia') && 'Sin referencia, el cliente que se registre desde este turno queda sin saber cómo llegó al estudio. '}
                        ¿Registrar el turno igual?
                    </p>
                </div>
                <div className={styles.modalFooter}>
                    <button className="rec-btn-secondary flex-fill" onClick={() => setDatosFaltantes(null)}>
                        Volver y completar
                    </button>
                    <button
                        className="rec-btn-accent flex-fill"
                        onClick={() => {
                            setDatosFaltantes(null);
                            setFaltantesConfirmados(true);
                            handleGuardar(false, false, true);
                        }}
                    >
                        <i className="bi bi-check2 me-1" />
                        Sí, registrar igual
                    </button>
                </div>
            </>
        );
    }

    if (diaNoLaborable) {
        return (
            <>
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderIzq}>
                        <i className="bi bi-slash-circle" />
                        <h5 className={styles.modalTitulo}>Ese día no se atiende</h5>
                    </div>
                    <div className={styles.modalHeaderAcciones}>
                        <button type="button" className={styles.modalHeaderBtn} onClick={onCerrar}>
                            <i className="bi bi-x-lg" />
                        </button>
                    </div>
                </div>
                <div className={styles.modalBody}>
                    <p style={{ fontSize: '0.9rem', color: '#334155' }}>
                        El <strong>{formatearFechaLarga(diaNoLaborable.fecha)}</strong> el estudio no atiende:{' '}
                        <strong>{diaNoLaborable.motivo}</strong>.
                    </p>
                    <p style={{ fontSize: '0.88rem', color: '#64748b' }}>
                        El bot no puede dar turnos ese día. Desde acá se puede agendar igual si es a propósito
                        —por ejemplo, alguien citado especialmente—, pero el cliente va a venir un día cerrado.
                    </p>
                    {errorForm && <div className="alert alert-danger py-2 mb-0">{errorForm}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <button className="rec-btn-secondary flex-fill" onClick={() => setDiaNoLaborable(null)} disabled={guardando}>
                        Elegir otro día
                    </button>
                    <button
                        className="rec-btn-accent flex-fill"
                        onClick={() => handleGuardar(false, true)}
                        disabled={guardando}
                    >
                        {guardando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-exclamation-diamond-fill me-1" />}
                        Sí, agendar igual
                    </button>
                </div>
            </>
        );
    }

    if (horarioCompleto) {
        return (
            <>
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderIzq}>
                        <i className="bi bi-exclamation-diamond-fill" />
                        <h5 className={styles.modalTitulo}>Horario completo</h5>
                    </div>
                    <div className={styles.modalHeaderAcciones}>
                        <button type="button" className={styles.modalHeaderBtn} onClick={onCerrar}>
                            <i className="bi bi-x-lg" />
                        </button>
                    </div>
                </div>
                <div className={styles.modalBody}>
                    <p style={{ fontSize: '0.9rem', color: '#334155' }}>
                        Este horario ya tiene <strong>{horarioCompleto.ocupados}/{horarioCompleto.maximo}</strong> turnos agendados.
                        ¿Desea agendarlo de todas formas como <strong>sobreturno</strong>?
                    </p>
                    {errorForm && <div className="alert alert-danger py-2 mb-0">{errorForm}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <button className="rec-btn-secondary flex-fill" onClick={() => setHorarioCompleto(null)} disabled={guardando}>
                        Cancelar
                    </button>
                    <button
                        className="rec-btn-accent flex-fill"
                        onClick={() => handleGuardar(true)}
                        disabled={guardando}
                    >
                        {guardando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-exclamation-diamond-fill me-1" />}
                        Sí, agendar como sobreturno
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <div className={styles.modalHeader}>
                <div className={styles.modalHeaderIzq}>
                    <i className={`bi ${modoEspontaneo ? 'bi-person-walking' : 'bi-calendar-plus'}`} />
                    <div>
                        <h5 className={styles.modalTitulo}>{modoEspontaneo ? 'Cliente sin turno' : 'Nuevo turno'}</h5>
                        <span className={styles.modalHeaderSubtitulo}>
                            {modoEspontaneo ? 'Registrá al cliente y elegí a qué área lo derivás' : 'Completá los datos para registrar un turno'}
                        </span>
                    </div>
                </div>
                <div className={styles.modalHeaderAcciones}>
                    <button type="button" className={styles.modalHeaderBtn} onClick={onCerrar}>
                        <i className="bi bi-x-lg" />
                    </button>
                </div>
            </div>

            <div className={styles.modalBody}>
                {/* En el ingreso espontáneo el turno entra siempre como sobreturno, así que el
                    aviso de "horario completo" no se dispara. Se muestra igual cuánta gente hay
                    en el bloque: es el dato que decide si conviene mandarlo a esta hora o a otra. */}
                {modoEspontaneo && ocupacion && (
                    <div className={`alert ${ocupacion.completo ? 'alert-warning' : 'alert-info'} py-2 px-3 mb-3`}
                         style={{ fontSize: '0.88rem' }}>
                        <i className={`bi ${ocupacion.completo ? 'bi-exclamation-diamond-fill' : 'bi-info-circle'} me-2`} />
                        Las <strong>{bloqueActual}</strong> ya tienen <strong>{ocupacion.ocupados}</strong> de{' '}
                        <strong>{ocupacion.cupo}</strong> turnos asignados.
                        {ocupacion.completo && ' Se va a cargar como sobreturno.'}
                    </div>
                )}

                <div className={styles.formSeccionTitulo}>Información del turno</div>

                {modoEspontaneo ? (
                    <div className={styles.celdaselBloque}>
                        <i className="bi bi-lightning-charge-fill me-1" />
                        Ingreso espontáneo — hoy, {bloqueActual || 'sin horario disponible'} (sobreturno)
                    </div>
                ) : celdaSel ? (
                    <div className={styles.celdaselBloque}>
                        <i className="bi bi-clock me-1" />
                        {celdaSel.fecha.toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })} — {celdaSel.horario}
                    </div>
                ) : (
                    <div className="row g-2 mb-3">
                        <div className="col-6">
                            <label className={`form-label fw-semibold ${styles.formLabel}`}>
                                <i className="bi bi-calendar-event me-1" />Fecha *
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                min={new Date().toISOString().split('T')[0]}
                                value={fechaSel}
                                onChange={e => setFechaSel(e.target.value)}
                            />
                        </div>
                        <div className="col-6">
                            <label className={`form-label fw-semibold ${styles.formLabel}`}>
                                <i className="bi bi-clock me-1" />Horario *
                            </label>
                            {cargandoHoras ? (
                                <div style={{ fontSize: '0.85rem', color: '#64748b', paddingTop: 8 }}>
                                    <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 1 }} /> Cargando…
                                </div>
                            ) : (
                                <select
                                    className="form-select"
                                    value={horarioSel}
                                    onChange={e => setHorarioSel(e.target.value)}
                                    disabled={horariosPosibles.length === 0}
                                >
                                    <option value="">
                                        {horariosPosibles.length === 0 ? 'Sin horarios configurados' : 'Seleccionar horario…'}
                                    </option>
                                    {horariosPosibles.map(h => <option key={h} value={h}>{h}</option>)}
                                </select>
                            )}
                        </div>
                    </div>
                )}

                <div className="mb-3">
                    <label className={`form-label fw-semibold ${styles.formLabel}`}>
                        <i className="bi bi-file-earmark-text me-1" />Tipo de trámite *
                    </label>
                    <select
                        className="form-select"
                        style={{ height: 44 }}
                        value={form.tipoTramite}
                        onChange={e => setForm(f => ({ ...f, tipoTramite: e.target.value }))}
                    >
                        {opcionesTipo.map(t => (
                            <option key={t.valor} value={t.valor}>{t.etiqueta}</option>
                        ))}
                    </select>
                </div>

                {modoEspontaneo && (
                    <div className="mb-3">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>
                            <i className="bi bi-signpost-2 me-1" />Área de atención *
                        </label>
                        <SelectorAreaAtencion
                            valor={form.atencion}
                            onChange={(valor) => {
                                setAreaTocada(true);
                                setForm(f => ({ ...f, atencion: valor }));
                            }}
                        />
                    </div>
                )}

                <div className={form.tipoTurno === 'VIDEOLLAMADA' ? 'mb-3' : 'mb-1'}>
                    <label className={`form-label fw-semibold ${styles.formLabel}`}>
                        <i className="bi bi-building me-1" />Modalidad *
                    </label>
                    <div className={styles.modalidadToggle}>
                        {TIPOS_TURNO.map(t => (
                            <button
                                type="button"
                                key={t.valor}
                                className={`${styles.modalidadBtn} ${form.tipoTurno === t.valor ? styles.modalidadBtnActivo : ''}`}
                                onClick={() => setForm(f => ({
                                    ...f,
                                    tipoTurno: t.valor,
                                    idUsuarioAtiende: t.valor === 'PRESENCIAL' ? '' : f.idUsuarioAtiende,
                                }))}
                            >
                                <i className={`bi ${t.icono}`} />
                                {t.etiqueta}
                            </button>
                        ))}
                    </div>
                </div>

                {form.tipoTurno === 'VIDEOLLAMADA' && (
                    <div className="mb-1">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>¿Quién atiende la videollamada? *</label>
                        {cargandoUsuarios ? (
                            <div style={{ fontSize: '0.85rem', color: '#64748b', paddingTop: 8 }}>
                                <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 1 }} /> Cargando usuarios…
                            </div>
                        ) : (
                            <select
                                className="form-select"
                                value={form.idUsuarioAtiende}
                                onChange={e => setForm(f => ({ ...f, idUsuarioAtiende: e.target.value }))}
                            >
                                <option value="">Seleccionar usuario…</option>
                                {usuarios.map(u => (
                                    <option key={u.id} value={u.id}>{u.nombre}</option>
                                ))}
                            </select>
                        )}
                    </div>
                )}

                <div className={styles.formSeccion}>
                    <div className={styles.formSeccionTitulo}>Datos del cliente</div>

                    {/* Buscar al cliente evita volver a tipear todo y deja el turno vinculado a su
                        CUIL. Aplica igual al que llega sin turno que al que saca uno con fecha:
                        en los dos casos suele ser alguien que ya está en el sistema. */}
                    <div className="mb-3">
                            {clienteVinculado ? (
                                <div className="d-flex align-items-center gap-2 p-2 rounded-3"
                                     style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
                                    <i className="bi bi-check-circle-fill text-success" />
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold">
                                            {apellidoNombre(clienteVinculado.apellido, clienteVinculado.nombre)}
                                        </div>
                                        <small className="text-muted">Cliente del estudio · {clienteVinculado.cuil}</small>
                                    </div>
                                    <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill"
                                            onClick={desvincularCliente}>
                                        Quitar
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <label className={`form-label fw-semibold ${styles.formLabel}`}>
                                        <i className="bi bi-search me-1" />¿Ya es cliente del estudio?
                                    </label>
                                    <input
                                        className="form-control"
                                        placeholder="Buscar por CUIL, nombre o apellido..."
                                        value={busquedaCliente}
                                        autoComplete="off"
                                        onChange={e => setBusquedaCliente(e.target.value)}
                                    />
                                    {buscandoCliente && <small className="text-muted d-block mt-1">Buscando...</small>}
                                    {!buscandoCliente && busquedaCliente.trim() && clientesEncontrados.length === 0 && (
                                        <small className="text-muted d-block mt-1">
                                            Sin resultados — completá los datos abajo para registrarlo.
                                        </small>
                                    )}
                                    {clientesEncontrados.length > 0 && (
                                        <ul className="list-group mt-1" style={{ maxHeight: 180, overflowY: 'auto' }}>
                                            {clientesEncontrados.map(cliente => (
                                                <li key={cliente.cuil}
                                                    className="list-group-item list-group-item-action"
                                                    role="button"
                                                    onClick={() => vincularCliente(cliente)}>
                                                    {apellidoNombre(cliente.apellido, cliente.nombre)}
                                                    <span className="text-muted"> · {cliente.cuil}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                            </>
                        )}
                    </div>

                    <div className="row g-4 mb-3">
                        <div className="col-6">
                            <label className={`form-label fw-semibold ${styles.formLabel}`}>
                                <i className="bi bi-person me-1" />Nombre *
                            </label>
                            <input className="form-control" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
                        </div>
                        <div className="col-6">
                            <label className={`form-label fw-semibold ${styles.formLabel}`}>
                                <i className="bi bi-person me-1" />Apellido *
                            </label>
                            <input className="form-control" value={form.apellido} onChange={e => setForm(f => ({ ...f, apellido: e.target.value }))} />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>
                            <i className="bi bi-card-text me-1" />CUIL <span className={styles.opcionalLabel}>(opcional)</span>
                        </label>
                        <input
                            className="form-control"
                            placeholder="Ej: 20-35123456-8"
                            value={form.cuil}
                            onChange={e => setForm(f => ({ ...f, cuil: e.target.value }))}
                        />
                    </div>

                    <div className="mb-1">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>
                            <i className="bi bi-telephone-fill me-1" />Teléfono <span className={styles.opcionalLabel}>(opcional)</span>
                        </label>
                        <input className="form-control" value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} placeholder="Ej: 3577421234" />
                    </div>

                    <div className="form-check mt-2">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="chkTieneCarpeta"
                            checked={!!form.tieneCarpeta}
                            onChange={e => setForm(f => ({
                                ...f,
                                tieneCarpeta: e.target.checked,
                                // Si ya tiene carpeta, no puede ser su primera vez en el estudio:
                                // se completa solo para que los dos campos no se contradigan.
                                esPrimeraVez: e.target.checked ? 'NO' : f.esPrimeraVez,
                            }))}
                        />
                        <label className="form-check-label" htmlFor="chkTieneCarpeta">
                            <i className="bi bi-folder-fill me-1" />Ya tiene carpeta (cliente existente del estudio)
                        </label>
                    </div>

                    <div className="mt-2">
                        <label className="form-label mb-1">
                            <i className="bi bi-person-plus me-1" />¿Es la primera vez que viene al estudio?
                        </label>
                        <select
                            className="form-select"
                            value={form.esPrimeraVez}
                            onChange={e => setForm(f => ({
                                ...f,
                                esPrimeraVez: e.target.value,
                                // Marcar "primera vez" contradice tener carpeta.
                                tieneCarpeta: e.target.value === 'SI' ? false : f.tieneCarpeta,
                            }))}
                        >
                            {/* Tres estados, no dos: "no se preguntó" no es lo mismo que "no". */}
                            <option value="">No se preguntó</option>
                            <option value="SI">Sí, es la primera vez</option>
                            <option value="NO">No, ya había venido</option>
                        </select>
                    </div>
                </div>

                <div className={styles.formSeccion}>
                    <div className={styles.formSeccionTitulo}>Información adicional</div>
                    <div className="mb-1">
                        <ComboboxDatosMaestros
                            label={
                                <>
                                    <i className="bi bi-chat-left-text-fill me-1" />Referencia <span className={styles.opcionalLabel}>(opcional)</span>
                                </>
                            }
                            placeholder="Ej: Facebook, Instagram, recomendado por X..."
                            opciones={referenciasDisponibles}
                            valor={form.referencia}
                            onChange={(nuevoNombre) => setForm(f => ({ ...f, ...resolverCambioReferencia(nuevoNombre, referenciasDisponibles) }))}
                            onCrearNuevo={(nuevaRef) => {
                                setReferenciasDisponibles(prev => [...prev, nuevaRef]);
                                setForm(f => ({ ...f, referencia: nuevaRef.nombre, idReferencia: nuevaRef.id }));
                            }}
                            tipo="referencia"
                        />
                    </div>
                    <div className="mb-1">
                        <label className={`form-label fw-semibold ${styles.formLabel}`}>
                            <i className="bi bi-chat-left-text me-1" />Motivo de consulta <span className={styles.opcionalLabel}>(opcional)</span>
                        </label>
                        <textarea
                            className="form-control"
                            rows={2}
                            placeholder="Por qué viene el cliente..."
                            value={form.motivo}
                            onChange={(e) => setForm(f => ({ ...f, motivo: e.target.value }))}
                        />
                    </div>
                </div>

                {errorForm && <div className="alert alert-danger py-2 mt-3 mb-0">{errorForm}</div>}
            </div>

            <div className={styles.modalFooter}>
                <button className="rec-btn-secondary flex-fill" onClick={onCerrar}>Cancelar</button>
                <button className="rec-btn-primary flex-fill" onClick={() => handleGuardar()} disabled={guardando}>
                    {guardando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1" />}
                    {modoEspontaneo ? 'Registrar y enviar a la cola' : 'Registrar turno'}
                </button>
            </div>
        </>
    );
}

// ── FiltrosCalendario ────────────────────────────────────────────────────────
/**
 * Buscar por persona y por referencia sin salir de la vista Calendario.
 *
 * Filtra las DOS partes de la vista a la vez —los contadores del mes y la cuadrilla semanal—
 * porque son la misma pregunta hecha con distinto zoom: "¿qué días de este mes tiene turno
 * Pérez?" se responde mirando el mes, y "¿a qué hora?" mirando la semana. Filtrar solo una de
 * las dos dejaría los números del mes contando turnos que la semana ya no muestra.
 *
 * No filtra lo que se le pide al backend: el rango que se trae es el mismo, esto solo decide
 * qué se dibuja. Así cambiar el filtro es instantáneo y no dispara una consulta por tecla.
 */
function FiltrosCalendario({ texto, onTexto, referencia, onReferencia, referencias, cantidadVisible, cantidadTotal }) {
    const hayFiltro = Boolean(texto || referencia);
    return (
        <div className={styles.filtrosAgenda}>
            <div className={styles.filtrosFila}>
                <div className={styles.filtroCampoAncho}>
                    <label className={styles.filtroLabel}>
                        <i className="bi bi-search me-2"></i>Buscar
                    </label>
                    <input
                        type="text"
                        className={`form-control ${styles.filtroInput}`}
                        placeholder="Nombre, CUIL, teléfono…"
                        value={texto}
                        onChange={(e) => onTexto(e.target.value)}
                    />
                </div>
                <SelectorFiltroReferencia
                    valor={referencia}
                    onChange={onReferencia}
                    referencias={referencias}
                />
                {hayFiltro && (
                    <button
                        type="button"
                        className={`rec-btn-secondary ${styles.filtroBotonLimpiar}`}
                        onClick={() => { onTexto(''); onReferencia(''); }}
                    >
                        Limpiar
                    </button>
                )}
            </div>
            {hayFiltro && (
                <div className={styles.filtroResumen}>
                    <i className="bi bi-funnel-fill me-2" />
                    {cantidadVisible === 0
                        ? 'Ningún turno del período coincide con el filtro.'
                        : `Mostrando ${cantidadVisible} de ${cantidadTotal} turno${cantidadTotal !== 1 ? 's' : ''} del período.`}
                </div>
            )}
        </div>
    );
}

// ── CalendarioView ───────────────────────────────────────────────────────────

function CalendarioView({ mes, turnosRango, diasCerrados, diaSeleccionado, onSeleccionarDia, onMesAnterior, onMesSiguiente }) {
    // Grilla: primer domingo del mes-grid hasta llenar 6 semanas
    const celdasGrid = useMemo(() => {
        const primerDia = new Date(mes.getFullYear(), mes.getMonth(), 1);
        const inicio = new Date(primerDia);
        inicio.setDate(1 - primerDia.getDay()); // getDay(): 0=dom, la columna 0 del grid

        const celdas = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(inicio);
            d.setDate(inicio.getDate() + i);
            celdas.push(d);
        }
        return celdas;
    }, [mes]);

    const turnosPorDia = useMemo(() => {
        const mapa = {};
        turnosRango.forEach(t => {
            if (!mapa[t.fechaTurno]) mapa[t.fechaTurno] = [];
            mapa[t.fechaTurno].push(t);
        });
        return mapa;
    }, [turnosRango]);

    const hoyISO = fechaToISO(new Date());
    const etiquetaMes = mes.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    const isoSeleccionado = diaSeleccionado ? fechaToISO(diaSeleccionado) : null;

    return (
        <div className={styles.calendarioWrapper}>
            {/* Cabecera de navegación del mes */}
            <div className={styles.calendarioNav}>
                <button className="btn btn-outline-secondary btn-sm" onClick={onMesAnterior}>
                    <i className="bi bi-chevron-left" />
                </button>
                <span className={styles.calendarioMesTitulo}>{etiquetaMes}</span>
                <button className="btn btn-outline-secondary btn-sm" onClick={onMesSiguiente}>
                    <i className="bi bi-chevron-right" />
                </button>
            </div>

            <div className={styles.calendarioGridWrapper}>
                <div className={styles.calendarioGrid}>
                    {/* Cabecera días semana */}
                    {COLS_CAL.map(d => (
                        <div key={d} className={styles.calendarioCabeceraDia}>{d}</div>
                    ))}

                    {/* Celdas */}
                    {celdasGrid.map((fecha) => {
                        const iso = fechaToISO(fecha);
                        const esEsteMes = fecha.getMonth() === mes.getMonth();
                        const esHoy = iso === hoyISO;
                        const esPasado = !esHoy && iso < hoyISO;
                        const turnos = turnosPorDia[iso] || [];
                        const esSel = isoSeleccionado === iso;
                        // Feriado o día marcado: se avisa acá porque es donde se elige el día.
                        const cerrado = diasCerrados?.get(iso);

                        return (
                            <div
                                key={iso}
                                className={`${styles.calendarioCelda} ${!esEsteMes ? styles.calendariaCeldaOtroMes : ''} ${esHoy ? styles.calendariaCeldaHoy : ''} ${esPasado ? styles.calendariaCeldaPasado : ''} ${esSel ? styles.calendariaCeldaSeleccionada : ''}`}
                                style={cerrado ? { background: '#fff7ed', borderColor: '#fdba74' } : undefined}
                                onClick={() => onSeleccionarDia(fecha)}
                                title={cerrado ? `No se atiende: ${cerrado.motivo}` : 'Ver este día en la agenda semanal'}
                            >
                                <span className={styles.calendariaNumeroDia}>{fecha.getDate()}</span>
                                {cerrado && (
                                    <span
                                        style={{
                                            display: 'block', fontSize: '0.62rem', fontWeight: 700, lineHeight: 1.15,
                                            color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.02em',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <i className="bi bi-slash-circle me-1" />
                                        {cerrado.motivo}
                                    </span>
                                )}
                                {turnos.length > 0 && (
                                    <div className={styles.calendarioContador}>
                                        <span
                                            className={styles.calendarioContadorCirculo}
                                            title={`${turnos.length} turno${turnos.length !== 1 ? 's' : ''}`}
                                        >
                                            {turnos.length}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ── AgendaSemanalView ────────────────────────────────────────────────────────
// Cuadrilla horarios × días de la semana. Las columnas son los días de atención
// configurados en el turnero (config.horariosPorDia[].activo) y las filas los bloques
// horarios de la jornada (propios de cada día, ver generarBloquesHorarios). Una celda puede
// contener más de un turno: maxTurnosPorHorario admite hasta 5 y los sobreturnos no tienen tope.

function AgendaSemanalView({
    inicioSemana, diaSeleccionado, config, turnosRango, diasCerrados,
    onSemanaAnterior, onSemanaSiguiente, onHoy, onAbrirDetalle, onCrearEnCelda,
}) {
    const dias = useMemo(() => {
        if (!config) return [];
        const todos = [];
        for (let i = 0; i < 7; i++) todos.push(sumarDias(inicioSemana, i));
        return todos.filter(d => horarioDelDia(config, d)?.activo);
    }, [inicioSemana, config]);

    const isosSemana = useMemo(() => new Set(dias.map(fechaToISO)), [dias]);

    // Slots que ofrece la config vigente, por día — cada día puede tener su propio horario/
    // almuerzo (ver TurneroHorarioDiaEntity), así que ya no hay un único set de slots común a
    // toda la semana. Mapa fechaISO -> Set("HH:mm" válidos para esa fecha puntual).
    const slotsConfigPorDia = useMemo(() => {
        const mapa = new Map();
        dias.forEach(d => mapa.set(fechaToISO(d), new Set(generarBloquesHorarios(config, d))));
        return mapa;
    }, [dias, config]);

    // Filas de la grilla = unión de los slots de config de todos los días visibles + los
    // horarios que realmente tienen los turnos de la semana. Muchos turnos caen fuera de la
    // grilla vigente (config cambiada después de agendarlos, importación del CSV histórico,
    // turnos ANSES que no validan grilla); si las filas salieran solo de la config, esos
    // turnos no se mostrarían en ningún lado.
    const filas = useMemo(() => {
        const set = new Set();
        slotsConfigPorDia.forEach(slots => slots.forEach(s => set.add(s)));
        turnosRango.forEach(t => {
            if (t.horario && isosSemana.has(t.fechaTurno)) set.add(t.horario);
        });
        return [...set].sort(); // "HH:mm" ordena lexicográficamente = cronológicamente
    }, [slotsConfigPorDia, turnosRango, isosSemana]);

    // Clave "fechaISO|HH:mm" → todos los turnos de ese bloque (no es uno solo).
    const turnosPorCelda = useMemo(() => {
        const mapa = new Map();
        turnosRango.forEach(t => {
            const clave = `${t.fechaTurno}|${t.horario}`;
            if (!mapa.has(clave)) mapa.set(clave, []);
            mapa.get(clave).push(t);
        });
        return mapa;
    }, [turnosRango]);

    const hoyISO = fechaToISO(new Date());
    const isoSeleccionado = diaSeleccionado ? fechaToISO(diaSeleccionado) : null;

    // "Semana del 17 al 23 de agosto" — si el rango cruza de mes, se nombran los dos.
    const etiquetaSemana = useMemo(() => {
        const fin = sumarDias(inicioSemana, 6);
        const mesIni = inicioSemana.toLocaleDateString('es-AR', { month: 'long' });
        const mesFin = fin.toLocaleDateString('es-AR', { month: 'long' });
        if (inicioSemana.getMonth() === fin.getMonth()) {
            return `Semana del ${inicioSemana.getDate()} al ${fin.getDate()} de ${mesIni}`;
        }
        return `Semana del ${inicioSemana.getDate()} de ${mesIni} al ${fin.getDate()} de ${mesFin}`;
    }, [inicioSemana]);

    return (
        <div className={styles.semanaWrapper}>
            <div className={styles.semanaNav}>
                <button className="btn btn-outline-secondary btn-sm" onClick={onSemanaAnterior} title="Semana anterior">
                    <i className="bi bi-chevron-left" />
                </button>
                <span className={styles.semanaTitulo}>{etiquetaSemana}</span>
                <button className="btn btn-outline-secondary btn-sm" onClick={onHoy} title="Ir a la semana actual">
                    Hoy
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={onSemanaSiguiente} title="Semana siguiente">
                    <i className="bi bi-chevron-right" />
                </button>
            </div>

            {!config ? (
                <div className={styles.semanaMensaje}>
                    <div className="spinner-border text-secondary" role="status" />
                </div>
            ) : (filas.length === 0 || dias.length === 0) ? (
                <div className={styles.semanaMensaje}>
                    <i className="bi bi-clock-history" style={{ fontSize: '1.8rem', color: '#cbd5e1', marginBottom: 8 }} />
                    <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.88rem' }}>
                        Configurá los horarios de atención en Configuración del Turnero.
                    </p>
                </div>
            ) : (
                <div className={styles.semanaGridWrapper}>
                    <div
                        className={styles.semanaGrid}
                        style={{
                            gridTemplateColumns: `78px repeat(${dias.length}, minmax(140px, 1fr))`,
                            minWidth: 78 + dias.length * 140,
                        }}
                    >
                        {/* Cabecera: esquina vacía + un encabezado por día */}
                        <div className={styles.semanaCabeceraEsquina} />
                        {dias.map(d => {
                            const iso = fechaToISO(d);
                            const cerrado = diasCerrados?.get(iso);
                            return (
                                <div
                                    key={iso}
                                    className={`${styles.semanaCabeceraDia} ${isoSeleccionado === iso ? styles.semanaCabeceraDiaSel : ''}`}
                                    style={cerrado ? { background: '#fff7ed', color: '#c2410c' } : undefined}
                                    title={cerrado ? `No se atiende: ${cerrado.motivo}` : undefined}
                                >
                                    <span>{NOMBRES_DIA[d.getDay()]}</span>
                                    <span className={styles.semanaCabeceraFecha}>
                                        {d.getDate()}/{String(d.getMonth() + 1).padStart(2, '0')}
                                        {iso === hoyISO && <span className={styles.semanaEtiquetaHoy}>HOY</span>}
                                    </span>
                                    {cerrado && (
                                        <span style={{ display: 'block', fontSize: '0.62rem', fontWeight: 700, color: '#c2410c' }}>
                                            <i className="bi bi-slash-circle me-1" />NO SE ATIENDE
                                        </span>
                                    )}
                                </div>
                            );
                        })}

                        {/* Una fila por bloque horario */}
                        {filas.map(slot => {
                            // "Fuera de grilla" ahora es por celda (día × horario), no por fila
                            // entera: un mismo horario puede ser válido un día y no otro.
                            const filaFueraDeTodos = !dias.some(d => slotsConfigPorDia.get(fechaToISO(d))?.has(slot));
                            return (
                            <React.Fragment key={slot}>
                                <div
                                    className={`${styles.semanaCeldaHora} ${filaFueraDeTodos ? styles.semanaCeldaHoraFuera : ''}`}
                                    title={filaFueraDeTodos ? 'Horario fuera de la grilla configurada actualmente' : undefined}
                                >
                                    {slot}
                                </div>
                                {dias.map(d => {
                                    const iso = fechaToISO(d);
                                    const esDeGrilla = slotsConfigPorDia.get(iso)?.has(slot) ?? false;
                                    const turnos = turnosPorCelda.get(`${iso}|${slot}`) || [];
                                    return (
                                        <div
                                            key={`${iso}|${slot}`}
                                            className={`${styles.semanaCelda} ${isoSeleccionado === iso ? styles.semanaCeldaSel : ''} ${esDeGrilla ? '' : styles.semanaCeldaFuera}`}
                                        >
                                            {turnos.length === 0 ? (
                                                // Solo se ofrece crear en los horarios que el backend acepta.
                                                esDeGrilla && (
                                                    <button
                                                        className={styles.semanaBotonVacio}
                                                        onClick={() => onCrearEnCelda(d, slot)}
                                                        title={`Nuevo turno · ${d.getDate()}/${d.getMonth() + 1} ${slot}`}
                                                    >
                                                        <i className="bi bi-plus" />
                                                    </button>
                                                )
                                            ) : (
                                                turnos.map(t => {
                                                    const e = estiloEstado(t.estado);
                                                    return (
                                                        <button
                                                            key={t.idTurno}
                                                            className={`${styles.semanaChipTurno} ${t.esSobreturno ? styles.semanaChipSobreturno : ''}`}
                                                            style={{
                                                                background: e.bg,
                                                                color: e.text,
                                                                borderColor: t.esSobreturno ? ESTILO_SOBRETURNO.border : e.border,
                                                            }}
                                                            onClick={() => onAbrirDetalle(t)}
                                                            title={`${apellidoNombre(t.apellido, t.nombre)} · ${e.etiqueta}`}
                                                        >
                                                            <span className={styles.semanaChipNombre}>
                                                                {t.tipoTurno === 'VIDEOLLAMADA' && <i className="bi bi-camera-video-fill me-1" />}
                                                                {apellidoNombre(t.apellido, t.nombre)}
                                                            </span>
                                                            <span className={styles.semanaChipSub}>
                                                                {t.esTramiteExpres ? etiquetaCortaMotivoExpres(t.expresMotivo, t.expresMotivoDetalle) : etiquetaTipo(t.tipoTramite)}
                                                                {t.esTramiteExpres ? ' · Trámite exprés' : t.esReingreso ? ' · Reingreso' : (t.esSobreturno && ' · Sobreturno')}
                                                            </span>
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── VistaDiaMobile ───────────────────────────────────────────────────────────
// Reemplazo mobile de AgendaSemanalView: en vez de una grilla de 7 columnas (que no entra en
// una pantalla angosta sin scroll lateral), muestra UN día como lista vertical de horarios,
// con navegación al día anterior/siguiente por botón o swipe horizontal. Reusa las mismas
// utilidades de slots/estilo que la grilla semanal (generarBloquesHorarios, horarioDelDia,
// estiloEstado) para que un turno se vea y filtre exactamente igual en las dos vistas.
function VistaDiaMobile({ dia, config, turnosRango, diasCerrados, onDiaAnterior, onDiaSiguiente, onAbrirDetalle, onCrearEnCelda }) {
    const iso = fechaToISO(dia);
    // Igual que en el calendario: se avisa que ese día no se atiende. Los horarios se siguen
    // mostrando porque el mostrador puede agendar igual confirmando, pero tiene que saberlo antes.
    const cerrado = diasCerrados?.get(iso);
    const hoyISO = fechaToISO(new Date());
    const esHoy = iso === hoyISO;
    const activo = horarioDelDia(config, dia)?.activo;

    // Slots del día: los que ofrece la config vigente + los horarios de turnos que ya
    // existen fuera de grilla (config cambiada después de agendarlos, importación histórica,
    // turnos ANSES) — mismo criterio que la unión de "filas" en AgendaSemanalView.
    const slots = useMemo(() => {
        if (!config || !activo) return [];
        const set = new Set(generarBloquesHorarios(config, dia));
        turnosRango.forEach((t) => { if (t.fechaTurno === iso && t.horario) set.add(t.horario); });
        return [...set].sort();
    }, [config, activo, dia, iso, turnosRango]);

    const turnosPorSlot = useMemo(() => {
        const mapa = new Map();
        turnosRango.forEach((t) => {
            if (t.fechaTurno !== iso) return;
            if (!mapa.has(t.horario)) mapa.set(t.horario, []);
            mapa.get(t.horario).push(t);
        });
        return mapa;
    }, [turnosRango, iso]);

    const etiquetaDia = dia.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });

    // Swipe horizontal simple: umbral de 50px para no confundir con un tap o con el scroll
    // vertical normal de la lista (solo se mide el eje X al soltar, nunca se bloquea el
    // scroll vertical mientras el dedo se mueve).
    const touchStartXRef = useRef(null);
    const handleTouchStart = (e) => { touchStartXRef.current = e.touches[0]?.clientX ?? null; };
    const handleTouchEnd = (e) => {
        const inicioX = touchStartXRef.current;
        touchStartXRef.current = null;
        if (inicioX == null) return;
        const finX = e.changedTouches[0]?.clientX ?? inicioX;
        const delta = finX - inicioX;
        if (Math.abs(delta) < 50) return;
        if (delta > 0) onDiaAnterior(); else onDiaSiguiente();
    };

    return (
        <div className={styles.semanaWrapper} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <div className={styles.semanaNav}>
                <button className="btn btn-outline-secondary btn-sm" onClick={onDiaAnterior} title="Día anterior" aria-label="Día anterior">
                    <i className="bi bi-chevron-left" />
                </button>
                <span className={styles.semanaTitulo} style={{ textTransform: 'capitalize' }}>
                    {etiquetaDia}{esHoy && ' · Hoy'}
                </span>
                <button className="btn btn-outline-secondary btn-sm" onClick={onDiaSiguiente} title="Día siguiente" aria-label="Día siguiente">
                    <i className="bi bi-chevron-right" />
                </button>
            </div>

            {cerrado && (
                <div
                    className="d-flex align-items-center gap-2"
                    style={{
                        background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10,
                        padding: '8px 12px', margin: '0 0 10px', color: '#c2410c', fontSize: '0.85rem',
                        fontWeight: 600,
                    }}
                >
                    <i className="bi bi-slash-circle" />
                    <span>No se atiende: {cerrado.motivo}</span>
                </div>
            )}

            {!config ? (
                <div className={styles.semanaMensaje}>
                    <div className="spinner-border text-secondary" role="status" />
                </div>
            ) : !activo ? (
                <div className={styles.semanaMensaje}>
                    <i className="bi bi-calendar-x" style={{ fontSize: '1.8rem', color: '#cbd5e1', marginBottom: 8 }} />
                    <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.88rem' }}>No hay atención configurada este día.</p>
                </div>
            ) : slots.length === 0 ? (
                <div className={styles.semanaMensaje}>
                    <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.88rem' }}>Sin horarios configurados.</p>
                </div>
            ) : (
                <div className="mob-dia-lista">
                    {slots.map((slot) => {
                        const turnos = turnosPorSlot.get(slot) || [];
                        return (
                            <div key={slot} className="mob-dia-fila">
                                <div className="mob-dia-hora">{slot}</div>
                                <div className="mob-dia-contenido">
                                    {turnos.length === 0 ? (
                                        <button
                                            type="button"
                                            className="mob-dia-boton-vacio mob-tocable"
                                            onClick={() => onCrearEnCelda(dia, slot)}
                                        >
                                            <i className="bi bi-plus-lg" aria-hidden="true" />
                                            Nuevo turno
                                        </button>
                                    ) : (
                                        turnos.map((t) => {
                                            const e = estiloEstado(t.estado);
                                            return (
                                                <button
                                                    key={t.idTurno}
                                                    type="button"
                                                    className="mob-dia-chip mob-tocable"
                                                    style={{
                                                        background: e.bg, color: e.text,
                                                        borderColor: t.esSobreturno ? ESTILO_SOBRETURNO.border : e.border,
                                                    }}
                                                    onClick={() => onAbrirDetalle(t)}
                                                >
                                                    <span className="mob-dia-chip-nombre">
                                                        {t.tipoTurno === 'VIDEOLLAMADA' && <i className="bi bi-camera-video-fill me-1" />}
                                                        {apellidoNombre(t.apellido, t.nombre)}
                                                    </span>
                                                    <span className="mob-dia-chip-sub">
                                                        {t.esTramiteExpres ? etiquetaCortaMotivoExpres(t.expresMotivo, t.expresMotivoDetalle) : etiquetaTipo(t.tipoTramite)}
                                                        {t.esTramiteExpres ? ' · Trámite exprés' : t.esReingreso ? ' · Reingreso' : (t.esSobreturno && ' · Sobreturno')}
                                                    </span>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── AgendaTurneroView ────────────────────────────────────────────────────────

// El filtro por fechas vive en el componente padre y no acá: a diferencia del texto y el
// estado (que se resuelven sobre lo ya cargado), elegir un día determina qué rango se le
// pide al backend — si no, un día del pasado nunca podría aparecer.
export function AgendaTurneroView({ turnosRango, cargandoRango, onAbrirDetalle, desde, hasta, onDesde, onHasta, referencias, referenciasPorId }) {
    const [filtroTexto, setFiltroTexto] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroReferencia, setFiltroReferencia] = useState('');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    // Buscar por persona sin período elegido busca en TODO el historial (backend). Antes solo
    // filtraba los turnos cargados (hoy + 30 días) y los anteriores no aparecían si no se ponía
    // desde/hasta. Con un día elegido sigue filtrando ese rango, como siempre.
    const buscaEnHistorial = !desde && filtroTexto.replace(/\s/g, '').length >= 3;
    const [resultadosHistorial, setResultadosHistorial] = useState(null);
    const [buscandoHistorial, setBuscandoHistorial] = useState(false);
    useEffect(() => {
        if (!buscaEnHistorial) {
            setResultadosHistorial(null);
            return undefined;
        }
        let cancelado = false;
        setBuscandoHistorial(true);
        // Espera a que se termine de tipear; turnosRango en las dependencias vuelve a buscar
        // después de editar un turno desde el detalle, para no mostrar el estado viejo.
        const espera = setTimeout(() => {
            buscarTurnosPorPersona(filtroTexto.trim())
                .then((r) => { if (!cancelado) setResultadosHistorial(r); })
                // Si falla, se cae al filtro local de siempre en vez de mostrar la lista vacía.
                .catch(() => { if (!cancelado) setResultadosHistorial(null); })
                .finally(() => { if (!cancelado) setBuscandoHistorial(false); });
        }, 350);
        return () => { cancelado = true; clearTimeout(espera); };
    }, [buscaEnHistorial, filtroTexto, turnosRango]);
    const enHistorial = buscaEnHistorial && resultadosHistorial !== null;

    const grupos = useMemo(() => {
        const q = filtroTexto.trim();
        const est = filtroEstado.trim().toUpperCase();
        const coincide = (t) => {
            const estado = !est || String(t?.estado || '').trim().toUpperCase() === est;
            // Sin "hasta", el rango es ese único día.
            const fecha = !desde || (t.fechaTurno >= desde && t.fechaTurno <= (hasta || desde));
            return coincideTextoTurno(t, q) && estado && fecha
                && coincideReferenciaTurno(t, filtroReferencia, referenciasPorId);
        };
        const mapa = new Map();
        (enHistorial ? resultadosHistorial : turnosRango).filter(coincide).forEach(t => {
            if (!mapa.has(t.fechaTurno)) mapa.set(t.fechaTurno, []);
            mapa.get(t.fechaTurno).push(t);
        });
        // En el historial lo más reciente primero: es lo que se busca casi siempre.
        return Array.from(mapa.entries()).sort(([a], [b]) => (enHistorial ? b.localeCompare(a) : a.localeCompare(b)));
    }, [turnosRango, enHistorial, resultadosHistorial, filtroTexto, filtroEstado, filtroReferencia, referenciasPorId, desde, hasta]);

    const hoyISO = fechaToISO(new Date());
    const mananaISO = fechaToISO(sumarDias(new Date(), 1));

    const etiquetaFecha = (iso) => {
        if (iso === hoyISO) return 'Hoy';
        if (iso === mananaISO) return 'Mañana';
        const d = isoAFecha(iso);
        // El año solo cuando no es el actual: en el historial aparecen turnos de otros años.
        const conAnio = d.getFullYear() !== new Date().getFullYear();
        return d.toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: 'long', ...(conAnio && { year: 'numeric' }) });
    };

    const hayFiltro = filtroTexto || filtroEstado || filtroReferencia || desde;

    const limpiarFiltros = () => {
        setFiltroTexto('');
        setFiltroEstado('');
        setFiltroReferencia('');
        onDesde('');
    };

    const etiquetaReferenciaFiltro = (val) => {
        if (val === SIN_REFERENCIA) return 'Sin referencia';
        if (val === REFERENCIA_LIBRE) return 'Anotada a mano';
        return referenciasPorId.get(String(val))?.nombre || 'Referencia';
    };

    const chipsFiltros = [
        filtroTexto && { key: 'texto', label: filtroTexto, onQuitar: () => setFiltroTexto('') },
        filtroEstado && {
            key: 'estado',
            label: FILTROS_ESTADO_AGENDA.find((f) => f.valor === filtroEstado)?.etiqueta || 'Estado',
            onQuitar: () => setFiltroEstado(''),
        },
        filtroReferencia && { key: 'referencia', label: etiquetaReferenciaFiltro(filtroReferencia), onQuitar: () => setFiltroReferencia('') },
        desde && { key: 'dia', label: hasta ? `${etiquetaFecha(desde)} → ${hasta}` : etiquetaFecha(desde), onQuitar: () => onDesde('') },
    ].filter(Boolean);

    const campoBuscar = (
        <div className={styles.filtroCampoAncho}>
            <label className={styles.filtroLabel}>
                <i className="bi bi-search me-2"></i>Buscar
            </label>
            <input
                type="text"
                className={`form-control ${styles.filtroInput}`}
                placeholder="Nombre, CUIL, teléfono…"
                value={filtroTexto}
                onChange={(e) => setFiltroTexto(e.target.value)}
            />
        </div>
    );
    const campoEstado = (
        <div className={styles.filtroCampo}>
            <label className={styles.filtroLabel}>
                <i className="bi bi-flag me-2"></i>Estado
            </label>
            <select
                className={`form-select ${styles.filtroInput}`}
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
            >
                {FILTROS_ESTADO_AGENDA.map((f) => (
                    <option key={f.valor || 'TODOS'} value={f.valor}>{f.etiqueta}</option>
                ))}
            </select>
        </div>
    );
    const campoReferencia = (
        <SelectorFiltroReferencia
            valor={filtroReferencia}
            onChange={setFiltroReferencia}
            referencias={referencias}
        />
    );
    const campoDia = (
        <div className={styles.filtroCampoDia}>
            <label className={styles.filtroLabel}>
                <i className="bi bi-calendar-event me-2"></i>Día
            </label>
            <div className={styles.filaDia}>
                <input
                    type="date"
                    className={`form-control ${styles.filtroInput} ${styles.filtroInputDia}`}
                    value={desde}
                    onChange={(e) => onDesde(e.target.value)}
                />
                {/* Atajos: los días que más se consultan son hoy y el siguiente. */}
                <button
                    type="button"
                    className={`${styles.atajoDia} ${desde === hoyISO ? styles.atajoDiaActivo : ''}`}
                    onClick={() => onDesde(desde === hoyISO ? '' : hoyISO)}
                >
                    Hoy
                </button>
                <button
                    type="button"
                    className={`${styles.atajoDia} ${desde === mananaISO ? styles.atajoDiaActivo : ''}`}
                    onClick={() => onDesde(desde === mananaISO ? '' : mananaISO)}
                >
                    Mañana
                </button>
            </div>
        </div>
    );
    // Solo tiene sentido acotar el final de un rango que ya tiene principio.
    const campoHasta = desde && (
        <div className={styles.filtroCampo}>
            <label className={styles.filtroLabel}>
                <i className="bi bi-calendar2-check me-2"></i>Hasta <span className={styles.filtroOpcional}>(opcional)</span>
            </label>
            <input
                type="date"
                className={`form-control ${styles.filtroInput}`}
                value={hasta}
                min={desde}
                onChange={(e) => onHasta(e.target.value)}
            />
        </div>
    );

    return (
        <div className={styles.agendaContainer}>
            {esMobile ? (
                <div className="mb-3">
                    <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                    <ChipsFiltros chips={chipsFiltros} />
                    {sheetFiltrosAbierto && (
                        <BottomSheet
                            titulo="Filtros"
                            onCerrar={() => setSheetFiltrosAbierto(false)}
                            accionPrimaria={hayFiltro ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                        >
                            <div className="mb-3">{campoBuscar}</div>
                            <div className="mb-3">{campoEstado}</div>
                            <div className="mb-3">{campoReferencia}</div>
                            <div className="mb-3">{campoDia}</div>
                            {campoHasta && <div>{campoHasta}</div>}
                        </BottomSheet>
                    )}
                </div>
            ) : (
                <div className={styles.filtrosAgenda}>
                    {/* Fila 1 — qué buscar */}
                    <div className={styles.filtrosFila}>
                        {campoBuscar}
                        {campoEstado}
                        {campoReferencia}
                    </div>

                    {/* Fila 2 — cuándo. El día puede ser pasado: se vuelve a consultar el backend. */}
                    <div className={styles.filtrosFila}>
                        {campoDia}
                        {campoHasta}

                        {hayFiltro && (
                            <button
                                type="button"
                                className={`rec-btn-secondary ${styles.filtroBotonLimpiar}`}
                                onClick={limpiarFiltros}
                            >
                                Limpiar
                            </button>
                        )}
                    </div>
                </div>
            )}

            {buscaEnHistorial && (
                <p className="text-muted small mb-2">
                    <i className="bi bi-clock-history me-1" />
                    Buscando en todo el historial, de lo más reciente a lo más viejo. Elegí un día para acotar.
                </p>
            )}

            {cargandoRango || (buscaEnHistorial && buscandoHistorial && !enHistorial) ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                    <p className="mt-2 text-muted">{buscaEnHistorial ? 'Buscando turnos…' : 'Cargando agenda…'}</p>
                </div>
            ) : grupos.length === 0 ? (
                <div className={styles.agendaVacia}>
                    <i className="bi bi-calendar-check" style={{ fontSize: '3rem', color: '#cbd5e1' }} />
                    <p style={{ color: '#94a3b8', marginTop: 12 }}>
                        {hayFiltro ? 'Ningún turno coincide con el filtro.' : 'No hay turnos entre hoy y los próximos 30 días. Elegí un día para consultar otro período.'}
                    </p>
                </div>
            ) : (
                grupos.map(([iso, turnos]) => (
                <div key={iso} className={styles.agendaGrupo}>
                    <div className={`${styles.agendaFechaHeader} ${iso === hoyISO ? styles.agendaFechaHoy : ''}`}>
                        <span className={styles.agendaFechaTexto}>{etiquetaFecha(iso)}</span>
                        <span className={styles.agendaFechaConteo}>{turnos.length} turno{turnos.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className={styles.agendaListaTurnos}>
                    {turnos.map(t => {
                        const e = estiloEstado(t.estado);
                        return (
                            <button
                                key={t.idTurno}
                                className={`rec-item ${styles.filaAgenda} ${t.esTramiteExpres ? styles.filaAgendaExpres : ''}`}
                                onClick={() => onAbrirDetalle(t)}
                            >
                                <RielTurno turno={t} colorEstado={e.riel} />
                                <div className={styles.filaAgendaHora}>
                                    <AccessTimeIcon fontSize="small" className="me-1" />
                                    {t.horario}
                                </div>
                                <div className={styles.filaAgendaCliente}>
                                    <div className={styles.filaAgendaNombre}>
                                        {t.tipoTurno === 'VIDEOLLAMADA' && <i className="bi bi-camera-video-fill me-1" style={{ color: '#3b82f6' }} title="Videollamada" />}
                                        {apellidoNombre(t.apellido, t.nombre)}
                                    </div>
                                    <div className={styles.filaAgendaSub}>
                                        {t.numeroIdentificacion && (
                                            <span className="d-inline-flex align-items-center">
                                                <BadgeIcon fontSize="small" className="me-1" />#{t.numeroIdentificacion}
                                            </span>
                                        )}
                                        {t.modalidad === 'ANSES' && (
                                            <span style={{ marginLeft: 6, fontWeight: 700, color: '#0369a1' }}>
                                                {esSinPresentacion(t.tipoTurnoAnses)
                                                    ? ` · ${metaTipoTurnoAnses(t.tipoTurnoAnses).enTurnero}`
                                                    : ` · Turno ANSES${t.horaTurnoAnses ? ` ${t.horaTurnoAnses}` : ''}`}
                                            </span>
                                        )}
                                        {t.esSobreturno && !t.esReingreso && !t.esTramiteExpres && <span style={{ marginLeft: 6, fontWeight: 700, color: ESTILO_SOBRETURNO.text }}>· Sobreturno</span>}
                                        <BadgeReingreso turno={t} className="ms-2" />
                                    </div>
                                    {t.motivo && (
                                        <div className={styles.filaAgendaMotivo} title={t.motivo}>
                                            <i className="bi bi-chat-left-quote-fill" />
                                            <span>{t.motivo}</span>
                                        </div>
                                    )}
                                </div>
                                {/* En el exprés, en lugar del tipo de trámite va por qué volvió. */}
                                {t.esTramiteExpres ? (
                                    <ChipMotivoExpres turno={t} />
                                ) : (
                                    <span className="rec-chip-tramite" style={{ background: bgTramite(t.tipoTramite), color: colorTramite(t.tipoTramite) }}>
                                        {etiquetaTipo(t.tipoTramite)}
                                    </span>
                                )}
                                <span className="rec-badge-estado" style={{ background: e.bg, border: `1px solid ${e.border}`, color: e.text }}>
                                    {e.etiqueta}
                                </span>
                            </button>
                        );
                    })}
                    </div>
                </div>
                ))
            )}
        </div>
    );
}

// ── ModalTurnosChatbot ───────────────────────────────────────────────────────
// Listado de turnos originados por el chatbot externo, más nuevos primero. El ojito reusa
// abrirDetalle/ModalDetalleTurno en vez de duplicar la vista de detalle.
const TURNOS_CHATBOT_POR_PAGINA = 50;

// oculto: se está viendo el detalle de un turno. El listado sigue montado (filtros, páginas
// cargadas y scroll intactos) y al volver queda donde estaba. actualizado / idEliminado: lo que
// cambió en ese detalle, para corregir la fila sin volver a pedir todo desde el principio.
function ModalTurnosChatbot({ onCerrar, onVerDetalle, oculto = false, actualizado = null, idEliminado = null }) {
    const [turnos, setTurnos] = useState([]);
    const cuerpoRef = useRef(null);
    const scrollGuardado = useRef(0);

    // display:none pierde la posición del scroll: se guarda al ocultarse y se devuelve al volver.
    React.useLayoutEffect(() => {
        if (!cuerpoRef.current) return;
        if (oculto) return;
        cuerpoRef.current.scrollTop = scrollGuardado.current;
    }, [oculto]);

    const verDetalle = (turno) => {
        if (cuerpoRef.current) scrollGuardado.current = cuerpoRef.current.scrollTop;
        onVerDetalle(turno);
    };

    useEffect(() => {
        if (actualizado) setTurnos(prev => prev.map(t => (t.idTurno === actualizado.idTurno ? { ...t, ...actualizado } : t)));
    }, [actualizado]);

    useEffect(() => {
        if (idEliminado != null) {
            setTurnos(prev => prev.filter(t => t.idTurno !== idEliminado));
            setTotal(n => Math.max(0, n - 1));
        }
    }, [idEliminado]);
    const [total, setTotal] = useState(0);
    const [hayMas, setHayMas] = useState(false);
    const [proximaPagina, setProximaPagina] = useState(0);
    const [cargando, setCargando] = useState(true);
    const [cargandoMas, setCargandoMas] = useState(false);
    const [error, setError] = useState('');
    const [filtroCliente, setFiltroCliente] = useState('');
    const [filtroFechaCreacion, setFiltroFechaCreacion] = useState('');

    // Debounce del texto: no golpear al backend en cada tecla que tipea.
    const [clienteDebounced, setClienteDebounced] = useState('');
    useEffect(() => {
        const id = setTimeout(() => setClienteDebounced(filtroCliente.trim()), 350);
        return () => clearTimeout(id);
    }, [filtroCliente]);

    // Cualquier cambio de filtro vuelve a la página 0: la lista acumulada de "cargar más" ya
    // no tiene sentido para un filtro distinto.
    useEffect(() => {
        let activo = true;
        setCargando(true);
        setError('');
        obtenerTurnosChatbot(0, TURNOS_CHATBOT_POR_PAGINA, { cliente: clienteDebounced, fechaCreacion: filtroFechaCreacion })
            .then(datos => {
                if (!activo) return;
                setTurnos(datos.turnos);
                setTotal(datos.total);
                setHayMas(datos.hayMas);
                setProximaPagina(1);
            })
            .catch(e => {
                console.error('Error al cargar los turnos del chatbot:', e);
                if (activo) setError('No se pudo cargar el listado de turnos del chatbot.');
            })
            .finally(() => { if (activo) setCargando(false); });
        return () => { activo = false; };
    }, [clienteDebounced, filtroFechaCreacion]);

    // Las páginas siguientes se agregan al final: la tabla ya viene del más nuevo al más viejo,
    // así que seguir leyendo hacia abajo es seguir yendo hacia atrás en el tiempo.
    const cargarMas = async () => {
        setCargandoMas(true);
        try {
            const datos = await obtenerTurnosChatbot(proximaPagina, TURNOS_CHATBOT_POR_PAGINA, { cliente: clienteDebounced, fechaCreacion: filtroFechaCreacion });
            setTurnos(previos => [...previos, ...datos.turnos]);
            setTotal(datos.total);
            setHayMas(datos.hayMas);
            setProximaPagina(p => p + 1);
        } catch (e) {
            console.error('Error al cargar más turnos del chatbot:', e);
            setError('No se pudieron cargar más turnos.');
        } finally {
            setCargandoMas(false);
        }
    };

    const hayFiltro = filtroCliente || filtroFechaCreacion;

    return (
        <div style={{ display: oculto ? 'none' : 'contents' }}>
            <div className={styles.modalHeader}>
                <div className={styles.modalHeaderIzq}>
                    <i className="bi bi-robot" />
                    <div>
                        <h5 className={styles.modalTitulo}>Turnos del chatbot</h5>
                        <span className={styles.modalHeaderSubtitulo}>
                            {cargando
                                ? 'Cargando…'
                                : `Mostrando ${turnos.length} de ${total} turno${total !== 1 ? 's' : ''}, del más nuevo al más viejo`}
                        </span>
                    </div>
                </div>
                <div className={styles.modalHeaderAcciones}>
                    <button type="button" className={styles.modalHeaderBtn} onClick={onCerrar}>
                        <i className="bi bi-x-lg" />
                    </button>
                </div>
            </div>

            <div className={styles.modalBody} ref={cuerpoRef}>
                <div className={styles.filtrosAgenda}>
                    <div className={styles.filtrosFila}>
                        <div className={styles.filtroCampoAncho}>
                            <label className={styles.filtroLabel}>
                                <i className="bi bi-search me-2"></i>Cliente
                            </label>
                            <input
                                type="text"
                                className={`form-control ${styles.filtroInput}`}
                                placeholder="Nombre o apellido…"
                                value={filtroCliente}
                                onChange={(e) => setFiltroCliente(e.target.value)}
                            />
                        </div>
                        <div className={styles.filtroCampo}>
                            <label className={styles.filtroLabel}>
                                <i className="bi bi-calendar-event me-2"></i>Creado el
                            </label>
                            <input
                                type="date"
                                className={`form-control ${styles.filtroInput}`}
                                value={filtroFechaCreacion}
                                onChange={(e) => setFiltroFechaCreacion(e.target.value)}
                            />
                        </div>
                        {hayFiltro && (
                            <button
                                type="button"
                                className={`rec-btn-secondary ${styles.filtroBotonLimpiar}`}
                                onClick={() => { setFiltroCliente(''); setFiltroFechaCreacion(''); }}
                            >
                                Limpiar
                            </button>
                        )}
                    </div>
                </div>

                {cargando ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" />
                    </div>
                ) : error ? (
                    <p className="text-danger text-center py-4">{error}</p>
                ) : turnos.length === 0 ? (
                    <div className={styles.agendaVacia}>
                        <i className="bi bi-robot" style={{ fontSize: '3rem', color: '#cbd5e1' }} />
                        <p style={{ color: '#94a3b8', marginTop: 12 }}>
                            {hayFiltro ? 'Ningún turno coincide con el filtro.' : 'Todavía no hay turnos cargados por el chatbot.'}
                        </p>
                    </div>
                ) : (
                    <div className={styles.tablaChatbotWrap}>
                        <table className={styles.tablaChatbot}>
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Teléfono</th>
                                    <th>Trámite</th>
                                    <th>Turno</th>
                                    <th>Estado</th>
                                    <th>Creado</th>
                                    <th className={styles.celdaAccion}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {turnos.map(t => {
                                    const e = estiloEstado(t.estado);
                                    return (
                                        <tr key={t.idTurno}>
                                            <td>
                                                <div>{apellidoNombre(t.apellido, t.nombre)}</div>
                                                {/* El motivo de consulta va acá y no en una columna
                                                    propia: es texto largo y una columna más obligaría
                                                    a scrollear de lado, que es justo lo que se sacó. */}
                                                {t.motivo && (
                                                    <div className={styles.filaChatbotMotivo} title={t.motivo}>
                                                        <i className="bi bi-chat-left-quote-fill me-1" />
                                                        {t.motivo}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{t.telefonoCliente || '-'}</td>
                                            <td>
                                                <span className="rec-chip-tramite" style={{ background: bgTramite(t.tipoTramite), color: colorTramite(t.tipoTramite) }}>
                                                    {etiquetaTipo(t.tipoTramite)}
                                                </span>
                                            </td>
                                            <td className={styles.celdaTurno}>
                                                {formatearFechaCorta(t.fechaTurno)} · <strong>{t.horario}</strong>
                                            </td>
                                            <td>
                                                <span className="rec-badge-estado" style={{ background: e.bg, border: `1px solid ${e.border}`, color: e.text }}>
                                                    {e.etiqueta}
                                                </span>
                                            </td>
                                            <td className={styles.celdaCreado}>{formatearFechaHoraCorta(t.creadoEn)}</td>
                                            <td className={styles.celdaAccion}>
                                                <button
                                                    type="button"
                                                    className={styles.botonOjo}
                                                    title="Ver detalle del turno"
                                                    onClick={() => verDetalle(t)}
                                                >
                                                    <i className="bi bi-eye-fill" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {hayMas && (
                            <div className="text-center py-3">
                                <button
                                    type="button"
                                    className="rec-btn-secondary"
                                    onClick={cargarMas}
                                    disabled={cargandoMas}
                                >
                                    {cargandoMas ? 'Cargando…' : `Ver más (quedan ${total - turnos.length})`}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── TurneroPage ──────────────────────────────────────────────────────────────

export default function TurneroPage() {
    const navigate = useNavigate();
    const { esMobile } = useBreakpoint();
    // Estado para vistas calendario y agenda
    const [vista, setVista] = useState('agenda');
    const [mesCalendario, setMesCalendario] = useState(() => {
        const hoy = new Date();
        return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    });
    const [turnosRango, setTurnosRango] = useState([]);
    // Días en que no se atiende, indexados por fecha ISO: feriados de la agenda + los que marcó
    // el estudio. Se usan para pintar el calendario y para avisar antes de cargar un turno.
    const [diasCerrados, setDiasCerrados] = useState(new Map());
    const [cargandoRango, setCargandoRango] = useState(false);

    // Catálogo de referencias para los filtros. Se carga una vez acá (y no dentro de cada
    // vista) porque lo comparten el calendario y la agenda.
    const [referencias, setReferencias] = useState([]);
    const [filtroCalTexto, setFiltroCalTexto] = useState('');
    const [filtroCalReferencia, setFiltroCalReferencia] = useState('');

    // Vista calendario: día resaltado en la agenda semanal y semana que ésta muestra.
    const [diaSeleccionado, setDiaSeleccionado] = useState(null);
    const [inicioSemana, setInicioSemana] = useState(() => domingoDeLaSemana(new Date()));
    const [configTurnero, setConfigTurnero] = useState(null);
    const refAgenda = useRef(null);

    // Rango de fechas elegido en la vista Agenda. Está acá y no en AgendaTurneroView porque
    // define qué se le pide al backend: sin esto, la lista solo podría filtrar dentro de los
    // próximos 30 días y los turnos del pasado serían inalcanzables.
    const [agendaDesde, setAgendaDesde] = useState('');
    const [agendaHasta, setAgendaHasta] = useState('');

    const [modalAbierto, setModalAbierto] = useState(false);
    const [celdaSel, setCeldaSel] = useState(null);
    const [turnoSel, setTurnoSel] = useState(null);
    const [avisoAsistio, setAvisoAsistio] = useState(null);
    const [modalChatbotAbierto, setModalChatbotAbierto] = useState(false);
    // Reingreso: buscador de atendidos de hoy (barra superior) y el turno elegido para el modal.
    const [buscadorReingresoAbierto, setBuscadorReingresoAbierto] = useState(false);
    const [turnoReingreso, setTurnoReingreso] = useState(null);
    // Turno cuyo detalle se abrió DESDE el listado de turnos del chatbot. Separado de turnoSel
    // a propósito: así "volver" desde el detalle solo vuelve a mostrar la lista (mismo modal,
    // mismo overlay) en vez de cerrar todo, sin tocar el flujo normal de calendario/agenda.
    const [turnoDetalleChatbot, setTurnoDetalleChatbot] = useState(null);
    // Lo que cambió en ese detalle, para que el listado (que sigue montado) corrija la fila.
    const [chatbotActualizado, setChatbotActualizado] = useState(null);
    const [chatbotIdEliminado, setChatbotIdEliminado] = useState(null);

    useEffect(() => {
        DatosMaestrosService.getReferencias()
            .then(res => setReferencias((res.data || []).slice().sort((a, b) =>
                String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'))))
            .catch(() => setReferencias([]));
    }, []);

    const referenciasPorId = useMemo(
        () => new Map(referencias.map(r => [String(r.id), r])),
        [referencias]
    );

    // Lo que realmente dibuja la vista Calendario: el mismo rango traído del backend, pasado
    // por los filtros. Se calcula una vez y lo comparten el mes y la agenda semanal.
    const turnosCalendario = useMemo(() => {
        const q = filtroCalTexto.trim();
        if (!q && !filtroCalReferencia) return turnosRango;
        return turnosRango.filter(t =>
            coincideTextoTurno(t, q) && coincideReferenciaTurno(t, filtroCalReferencia, referenciasPorId));
    }, [turnosRango, filtroCalTexto, filtroCalReferencia, referenciasPorId]);

    const cargarRango = useCallback(async (desde, hasta, incluirCancelados = false) => {
        setCargandoRango(true);
        try {
            // Los turnos y los días cerrados del MISMO rango, juntos: el calendario los dibuja en
            // la misma pasada y así no se ve un parpadeo con los feriados sin marcar.
            const [datos, cerrados] = await Promise.all([
                listarRango(fechaToISO(desde), fechaToISO(hasta), incluirCancelados),
                listarDiasNoLaborables(fechaToISO(desde), fechaToISO(hasta)).catch(() => []),
            ]);
            setTurnosRango(datos);
            setDiasCerrados(new Map(cerrados.map(d => [d.fecha, d])));
        } catch (e) {
            console.error('Error al cargar rango:', e);
        } finally {
            setCargandoRango(false);
        }
    }, []);

    // La config del turnero (días de atención y grilla horaria) no cambia durante la sesión;
    // se carga una sola vez y la usa la agenda semanal para armar columnas y filas.
    useEffect(() => {
        obtenerConfigTurnero()
            .then(setConfigTurnero)
            .catch(e => console.error('Error al cargar la config del turnero:', e));
    }, []);

    // En la vista calendario conviven el mes y la semana de la agenda, y la semana puede
    // caer fuera del mes visible: se pide el rango que cubre a ambos.
    const cargarVistaActual = useCallback(() => {
        if (vista === 'calendario') {
            const inicioMes = new Date(mesCalendario.getFullYear(), mesCalendario.getMonth(), 1);
            const finMes = new Date(mesCalendario.getFullYear(), mesCalendario.getMonth() + 1, 0);
            const finSemana = sumarDias(inicioSemana, 6);
            cargarRango(
                inicioSemana < inicioMes ? inicioSemana : inicioMes,
                finSemana > finMes ? finSemana : finMes,
            );
        } else if (agendaDesde) {
            // Rango elegido en el filtro; sin "hasta" es ese único día. Puede ser pasado. Como lo
            // eligió el usuario, se traen también los cancelados viejos (la agenda por defecto los esconde).
            cargarRango(isoAFecha(agendaDesde), isoAFecha(agendaHasta || agendaDesde), true);
        } else {
            cargarRango(new Date(), sumarDias(new Date(), 30));
        }
    }, [vista, mesCalendario, inicioSemana, agendaDesde, agendaHasta, cargarRango]);

    useEffect(() => {
        cargarVistaActual();
    }, [cargarVistaActual]);

    const cerrarModal = () => {
        setModalAbierto(false);
        setCeldaSel(null);
        setTurnoSel(null);
    };

    // Sin argumentos (botón "Nuevo turno") el modal pide fecha y horario; desde una celda
    // vacía de la agenda semanal llegan preseleccionados.
    const abrirCrear = (fecha = null, horario = null) => {
        setCeldaSel(fecha && horario ? { fecha, horario } : null);
        setTurnoSel(null);
        setModalAbierto(true);
    };

    // Clic en un día del calendario mensual: la agenda semanal salta a esa semana, resalta
    // la columna y la página baja hasta ella.
    const seleccionarDia = (fecha) => {
        setDiaSeleccionado(fecha);
        setInicioSemana(domingoDeLaSemana(fecha));
        refAgenda.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Calendario y agenda se mueven juntos: si quedaran desacoplados, el rango pedido al
    // backend (mes ∪ semana) se estiraría tanto como la distancia entre ambos.

    // Cambiar de mes lleva la agenda a la semana de hoy si es el mes actual, o a la
    // primera semana del mes en cualquier otro caso.
    const irAMes = (nuevoMes) => {
        const hoy = new Date();
        const esMesActual = nuevoMes.getFullYear() === hoy.getFullYear() && nuevoMes.getMonth() === hoy.getMonth();
        setMesCalendario(nuevoMes);
        setInicioSemana(domingoDeLaSemana(esMesActual ? hoy : nuevoMes));
    };

    // Cambiar de semana arrastra el calendario al mes de esa semana. Para una semana
    // partida entre dos meses manda el miércoles, que es su punto medio.
    const irASemana = (nuevoDomingo) => {
        setInicioSemana(nuevoDomingo);
        const miercoles = sumarDias(nuevoDomingo, 3);
        setMesCalendario(m => (
            m.getFullYear() === miercoles.getFullYear() && m.getMonth() === miercoles.getMonth()
                ? m
                : new Date(miercoles.getFullYear(), miercoles.getMonth(), 1)
        ));
    };

    // Día a día (VistaDiaMobile): mismo criterio de sincronización que irASemana, pero sin el
    // desempate del miércoles — acá alcanza con el mes del propio día elegido.
    const irADia = (nuevoDia) => {
        setDiaSeleccionado(nuevoDia);
        setInicioSemana(domingoDeLaSemana(nuevoDia));
        setMesCalendario(m => (
            m.getFullYear() === nuevoDia.getFullYear() && m.getMonth() === nuevoDia.getMonth()
                ? m
                : new Date(nuevoDia.getFullYear(), nuevoDia.getMonth(), 1)
        ));
    };

    // Limpiar el día borra también el "hasta" (sin principio no hay rango), y si el nuevo
    // día deja el "hasta" en el pasado el rango quedaría invertido, así que se descarta.
    const cambiarAgendaDesde = (valor) => {
        setAgendaDesde(valor);
        if (!valor || (agendaHasta && agendaHasta < valor)) setAgendaHasta('');
    };

    const abrirDetalle = (turno) => {
        setTurnoSel(turno);
        setCeldaSel(null);
        setModalAbierto(true);
    };

    const verDetalleDesdeChatbot = (turno) => {
        setTurnoDetalleChatbot(turno);
    };

    const handleTurnoCreado = () => {
        cerrarModal();
        cargarVistaActual();
    };

    const handleTurnoActualizado = (actualizado) => {
        setTurnoSel(actualizado);
        setTurnosRango(prev => prev.map(t => t.idTurno === actualizado.idTurno ? actualizado : t));
    };

    const handleTurnoEliminado = () => {
        cerrarModal();
        cargarVistaActual();
    };

    const handleAsistioRegistrar = (turno) => {
        cerrarModal();
        setAvisoAsistio(turno);
    };

    // Mismos tres casos que handleTurnoActualizado/Eliminado/AsistioRegistrar, pero para el
    // detalle abierto desde "Turnos del chatbot": on Actualizado se queda en el detalle (con los
    // datos frescos) en vez de cerrar. El listado queda montado detrás (con su scroll y lo que ya
    // se había cargado) y corrige la fila del turno que cambió.
    const handleTurnoActualizadoChatbot = (actualizado) => {
        setTurnoDetalleChatbot(actualizado);
        setChatbotActualizado(actualizado);
        setTurnosRango(prev => prev.map(t => t.idTurno === actualizado.idTurno ? actualizado : t));
    };

    const handleTurnoEliminadoChatbot = () => {
        if (turnoDetalleChatbot) setChatbotIdEliminado(turnoDetalleChatbot.idTurno);
        setTurnoDetalleChatbot(null);
        cargarVistaActual();
    };

    const handleAsistioRegistrarChatbot = (turno) => {
        setTurnoDetalleChatbot(null);
        setModalChatbotAbierto(false);
        setAvisoAsistio(turno);
    };

    // "Volvió cliente" desde el detalle de un turno finalizado o desde el buscador de la barra: se
    // cierra el overlay de origen antes de abrir ModalReingresoCliente (Bootstrap), que si no
    // quedaría tapado por el overlay del turnero.
    const abrirReingreso = (turno) => {
        cerrarModal();
        setTurnoDetalleChatbot(null);
        setModalChatbotAbierto(false);
        setBuscadorReingresoAbierto(false);
        setTurnoReingreso(turno);
    };

    const handleReingresoRegistrado = () => {
        setTurnoReingreso(null);
        cargarVistaActual();
    };

    const VISTAS = [
        { id: 'calendario', icono: 'bi-calendar-month', label: 'Calendario' },
        { id: 'agenda',    icono: 'bi-list-ul',         label: 'Agenda' },
    ];

    return (
        <div className="homepage-container area-recepcion">
            <PageHeader
                variante="plano"
                icono="calendar3"
                titulo="Turnero"
                descripcion="Gestión de turnos de atención"
            />

            <div className={`${styles.accionesBarra} mob-grid-2col`}>
                <button className="rec-btn-primary d-flex align-items-center gap-2" onClick={() => abrirCrear()}>
                    <i className="bi bi-calendar-plus" />
                    Nuevo turno
                </button>
                <button className="rec-btn-accent d-flex align-items-center gap-2" onClick={() => setBuscadorReingresoAbierto(true)}>
                    <i className="bi bi-arrow-repeat" />
                    Volvió cliente
                </button>
                <button className="rec-btn-secondary d-flex align-items-center gap-2" onClick={() => navigate('/recepcion')}>
                    <i className="bi bi-speedometer2" />
                    Ir a Panel Diario
                </button>
                <button className="rec-btn-secondary d-flex align-items-center gap-2" onClick={() => navigate('/recepcion/atencion')}>
                    <i className="bi bi-arrow-right-circle-fill" />
                    Ir a cola de clientes
                </button>
                <button className="rec-btn-secondary d-flex align-items-center gap-2" onClick={() => { setChatbotActualizado(null); setChatbotIdEliminado(null); setModalChatbotAbierto(true); }}>
                    <i className="bi bi-robot" />
                    Turnos del chatbot
                </button>
            </div>

            <div className="eventos-section rec-panel">
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-list-ul me-2"></i>
                        Turnos
                    </h2>
                    <div className={styles.vistaSelector}>
                        {VISTAS.map(v => (
                            <button
                                key={v.id}
                                className={`${styles.vistaBoton} ${vista === v.id ? styles.vistaBotonActivo : ''}`}
                                onClick={() => setVista(v.id)}
                            >
                                <i className={`bi ${v.icono} me-1`} />
                                {v.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Vista Calendario: mes arriba, agenda semanal debajo */}
                {vista === 'calendario' && (
                    <>
                        <FiltrosCalendario
                            texto={filtroCalTexto}
                            onTexto={setFiltroCalTexto}
                            referencia={filtroCalReferencia}
                            onReferencia={setFiltroCalReferencia}
                            referencias={referencias}
                            cantidadVisible={turnosCalendario.length}
                            cantidadTotal={turnosRango.length}
                        />
                        <CalendarioView
                            mes={mesCalendario}
                            turnosRango={turnosCalendario}
                            diasCerrados={diasCerrados}
                            diaSeleccionado={diaSeleccionado}
                            onSeleccionarDia={seleccionarDia}
                            onMesAnterior={() => irAMes(new Date(mesCalendario.getFullYear(), mesCalendario.getMonth() - 1, 1))}
                            onMesSiguiente={() => irAMes(new Date(mesCalendario.getFullYear(), mesCalendario.getMonth() + 1, 1))}
                        />
                        <div ref={refAgenda} className={styles.semanaSeparador}>
                            <h3 className={styles.semanaSeccionTitulo}>
                                <i className="bi bi-calendar-week" />
                                {esMobile ? 'Agenda del día' : 'Agenda semanal'}
                            </h3>
                            {esMobile ? (
                                <VistaDiaMobile
                                    dia={diaSeleccionado || new Date()}
                                    config={configTurnero}
                                    turnosRango={turnosCalendario}
                                    diasCerrados={diasCerrados}
                                    onDiaAnterior={() => irADia(sumarDias(diaSeleccionado || new Date(), -1))}
                                    onDiaSiguiente={() => irADia(sumarDias(diaSeleccionado || new Date(), 1))}
                                    onAbrirDetalle={abrirDetalle}
                                    onCrearEnCelda={abrirCrear}
                                />
                            ) : (
                                <AgendaSemanalView
                                    inicioSemana={inicioSemana}
                                    diaSeleccionado={diaSeleccionado}
                                    config={configTurnero}
                                    turnosRango={turnosCalendario}
                                    diasCerrados={diasCerrados}
                                    onSemanaAnterior={() => irASemana(sumarDias(inicioSemana, -7))}
                                    onSemanaSiguiente={() => irASemana(sumarDias(inicioSemana, 7))}
                                    onHoy={() => irASemana(domingoDeLaSemana(new Date()))}
                                    onAbrirDetalle={abrirDetalle}
                                    onCrearEnCelda={abrirCrear}
                                />
                            )}
                        </div>
                    </>
                )}

                {/* Vista Agenda */}
                {vista === 'agenda' && (
                    <AgendaTurneroView
                        turnosRango={turnosRango}
                        referencias={referencias}
                        referenciasPorId={referenciasPorId}
                        cargandoRango={cargandoRango}
                        onAbrirDetalle={abrirDetalle}
                        desde={agendaDesde}
                        hasta={agendaHasta}
                        onDesde={cambiarAgendaDesde}
                        onHasta={setAgendaHasta}
                    />
                )}

                <div className={styles.leyendaEstados}>
                    {ESTADOS_TURNERO.map(e => (
                        <BadgeEstado key={e.valor} estado={e.valor} tamaño="pequeño" />
                    ))}
                    <BadgeSobreturno tamaño="pequeño" />
                    <BadgeReingreso turno={{ esReingreso: true }} />
                    <BadgeReingreso turno={{ esTramiteExpres: true }} />
                </div>
            </div>

            {/* Modal crear / detalle */}
            {modalAbierto && (
                <div className={styles.overlay} onClick={cerrarModal}>
                    <div
                        className={`${styles.contenidoModal} ${turnoSel ? styles.contenidoModalDetalle : styles.contenidoModalAncho}`}
                        onClick={e => e.stopPropagation()}
                    >
                        {turnoSel ? (
                            <ModalDetalleTurno
                                turnoSel={turnoSel}
                                onCerrar={cerrarModal}
                                onActualizado={handleTurnoActualizado}
                                onEliminado={handleTurnoEliminado}
                                onAsistioRegistrar={handleAsistioRegistrar}
                                onVolvioCliente={abrirReingreso}
                            />
                        ) : (
                            <ModalCrearTurno
                                celdaSel={celdaSel}
                                onCerrar={cerrarModal}
                                onCreado={handleTurnoCreado}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Modal: turnos originados por el chatbot (y su detalle, en el mismo overlay) */}
            {modalChatbotAbierto && (
                <div
                    className={styles.overlay}
                    onClick={() => { setModalChatbotAbierto(false); setTurnoDetalleChatbot(null); }}
                >
                    <div
                        className={`${styles.contenidoModal} ${turnoDetalleChatbot ? styles.contenidoModalDetalle : styles.contenidoModalChatbot}`}
                        onClick={e => e.stopPropagation()}
                    >
                        {turnoDetalleChatbot && (
                            <ModalDetalleTurno
                                turnoSel={turnoDetalleChatbot}
                                onCerrar={() => setTurnoDetalleChatbot(null)}
                                onActualizado={handleTurnoActualizadoChatbot}
                                onEliminado={handleTurnoEliminadoChatbot}
                                onAsistioRegistrar={handleAsistioRegistrarChatbot}
                                onVolvioCliente={abrirReingreso}
                            />
                        )}
                        {/* Montado también mientras se ve un detalle: así al volver sigue en el mismo lugar. */}
                        <ModalTurnosChatbot
                            oculto={Boolean(turnoDetalleChatbot)}
                            actualizado={chatbotActualizado}
                            idEliminado={chatbotIdEliminado}
                            onCerrar={() => setModalChatbotAbierto(false)}
                            onVerDetalle={verDetalleDesdeChatbot}
                        />
                    </div>
                </div>
            )}

            {/* Reingreso: buscador de atendidos de hoy + modal de retorno */}
            {buscadorReingresoAbierto && (
                <div className={styles.overlay} onClick={() => setBuscadorReingresoAbierto(false)}>
                    <div className={`${styles.contenidoModal} ${styles.contenidoModalAncho}`} onClick={e => e.stopPropagation()}>
                        <ModalBuscarAtendidoHoy
                            subtitulo="Buscá al cliente entre los atendidos de hoy o en todo el estudio"
                            onCerrar={() => setBuscadorReingresoAbierto(false)}
                            onElegir={abrirReingreso}
                            onElegirCliente={async (cliente) => abrirReingreso(await ultimaAtencionFinalizada(cliente))}
                        />
                    </div>
                </div>
            )}

            <ModalReingresoCliente
                show={!!turnoReingreso}
                turnoOrigen={turnoReingreso}
                onCerrar={() => setTurnoReingreso(null)}
                onRegistrado={handleReingresoRegistrado}
            />

            {/* Aviso: turno marcado como Asistió */}
            {avisoAsistio && (
                <div className={styles.overlayPrompt}>
                    <div className={styles.contenidoPrompt}>
                        <div className={styles.promptIcono}>
                            <i className="bi bi-check-circle-fill" style={{ color: '#16a34a' }} />
                        </div>
                        <h5 className={styles.promptTitulo}>Turno marcado como Asistió</h5>
                        <p className={styles.promptSubtitulo}>
                            <strong>{apellidoNombre(avisoAsistio.apellido, avisoAsistio.nombre)}</strong>
                            {avisoAsistio.numeroIdentificacion && (
                                <span className={styles.promptNroId}> — #{avisoAsistio.numeroIdentificacion}</span>
                            )}
                        </p>
                        <p className={styles.promptTexto}>
                            Cliente confirmado, esperando atención.
                        </p>
                        <button className="rec-btn-primary" onClick={() => setAvisoAsistio(null)}>
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
